/**
 * weatherRasterLayer.ts — WebGL2 weather raster as a MapLibre CustomLayer.
 *
 * Renders directly into MapLibre's GL context using the map's own projection
 * matrix, which means the layer follows tilt and rotation correctly without
 * any custom matrix code. The raster is drawn as an N×N grid of vertices
 * (not a single quad) so perspective foreshortening interpolates smoothly
 * across the surface — without the grid, a tilted view would show the
 * raster as a flat plane intersecting the basemap instead of lying on it.
 *
 * Core "Windy smoothness" technique: interpolate the *raw data value* between
 * two frames using mix(), then apply the palette. Intermediate values produce
 * intermediate colors — no banding, continuous motion.
 *
 *     x = mix(tex0.value, tex1.value, time)     // per-pixel blend
 *     color = palette(x)                         // colorize after blend
 *
 * Precip-type layers (MRMS) use the same pipeline: the byte value encodes both
 * category and intensity, and the palette is a flat 256-entry LUT with
 * transition zones at the category boundaries so blurred/interpolated values
 * at the edges produce plausible colors.
 *
 * Lifecycle (MapLibre CustomLayerInterface):
 *   constructor(opts)        — record config; defer all GL work
 *   onAdd(map, gl)           — compile shaders, build palette/grid VAO
 *   render(gl, args)         — bind program, set uniforms, draw the grid
 *   onRemove(map, gl)        — release shaders, textures, buffers
 *
 * The class is added to the map via `map.addLayer(layer)` and removed via
 * `map.removeLayer(layer.id)` — there is no separate canvas in the DOM.
 */

import { MercatorCoordinate } from "maplibre-gl";
import type { Map as MaplibreMap, CustomLayerInterface as MaplibreCustomLayer } from "maplibre-gl";
import type { LayerConfig } from "./weatherLayerConfig";
import { buildPaletteTexture, buildPrecipTypeLUT } from "./weatherLayerConfig";
import { estimateMotion, debugVisualize, type MotionField } from "./weatherMotionEstimator";

// ─── Shaders ────────────────────────────────────────────────────────────────

const VERTEX_SHADER = `#version 300 es
precision highp float;

in vec2 a_position;      // unit quad [0,1]^2
uniform vec4 u_quadBbox; // (xMin, yMin, xMax, yMax) in unit-mercator
uniform mat4 u_mvp;      // mercator → clip-space

out vec2 v_uv;

void main() {
  vec2 mercPos = mix(u_quadBbox.xy, u_quadBbox.zw, a_position);
  v_uv = a_position;  // 0,0 = NW of frame bbox; 1,1 = SE
  gl_Position = u_mvp * vec4(mercPos, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = (defines: {
  DECODE_MODE: number;
  SMOOTH: number;
  BAND_CHANNEL: number;
  DISCARD_ZERO: number;
  RGB_PRECIP: number;
  QUANT_254: number;
}) => `#version 300 es
precision highp float;
precision highp sampler2D;

#define DECODE_MODE ${defines.DECODE_MODE}
#define SMOOTH ${defines.SMOOTH}
#define BAND_CHANNEL ${defines.BAND_CHANNEL}
#define DISCARD_ZERO ${defines.DISCARD_ZERO}
#define RGB_PRECIP ${defines.RGB_PRECIP}
#define QUANT_254 ${defines.QUANT_254}

uniform sampler2D u_tex0;
uniform sampler2D u_tex1;
uniform sampler2D u_palette;
uniform float u_time;
uniform float u_opacity;
// 1.0 when the scrubber is being driven by the playback loop, 0.0 when the
// playhead is parked. Used by SMOOTH==5 to raise the LUT alpha cut during
// playback so light-precip pixels that pulse across the noise floor between
// frames are suppressed; the static-paused look stays sensitive to drizzle.
uniform float u_animating;
uniform vec2 u_texSize;
// Multiplier on the blur kernel, set per-frame from the JS side based on
// map zoom. The kernel offsets are written for ~zoom 6 (1 source texel ≈
// 1 screen pixel for MRMS at downsample 2). At higher zoom each source
// texel covers many screen pixels, so without this scale the kernel is too
// tight to hide the texel grid. JS computes pow(2, zoom - kernelRefZoom)
// and clamps to a safe range. 1.0 = no change, 2.0 = doubles the kernel
// radius, etc. Used by both SMOOTH==1 (continuous layers like EU radar
// and satellite) and SMOOTH==2 (precip-type encoded layers like MRMS).
uniform float u_kernelScale;
// Motion flow texture — small RGBA grid where:
//   R: (pixel-space dx + maxDisp) encoded as 0..255, centered at 127
//   G: (pixel-space dy + maxDisp) encoded as 0..255, centered at 127
//   B: confidence 0..255
// The layer only binds a real texture here for SMOOTH==3 (motion-warped
// mode); other modes get a dummy 1×1 texture to keep WebGL happy.
uniform sampler2D u_flow;
uniform float u_flowMaxDisp;  // Max displacement in source pixels (matches MOTION_CONFIG.maxDisplacement)

in vec2 v_uv;
out vec4 outColor;

#if RGB_PRECIP == 1
vec3 bicubicSample(sampler2D tex, vec2 uv, vec2 texSize) {
  vec2 invTex = 1.0 / texSize;
  vec2 pos = uv * texSize - 0.5;
  vec2 f = fract(pos);
  vec2 f2 = f * f;
  vec2 f3 = f2 * f;
  vec2 w0 = -0.5 * f3 + f2 - 0.5 * f;
  vec2 w1 =  1.5 * f3 - 2.5 * f2 + 1.0;
  vec2 w2 = -1.5 * f3 + 2.0 * f2 + 0.5 * f;
  vec2 w3 =  0.5 * f3 - 0.5 * f2;
  vec2 s0 = w0 + w1;
  vec2 s1 = w2 + w3;
  vec2 f0 = w1 / s0;
  vec2 f1 = w3 / s1;
  vec2 t0 = (floor(pos) - 0.5 + f0) * invTex;
  vec2 t1 = (floor(pos) + 1.5 + f1) * invTex;
  return (texture(tex, vec2(t0.x, t0.y)).rgb * s0.x
        + texture(tex, vec2(t1.x, t0.y)).rgb * s1.x) * s0.y
       + (texture(tex, vec2(t0.x, t1.y)).rgb * s0.x
        + texture(tex, vec2(t1.x, t1.y)).rgb * s1.x) * s1.y;
}
#endif

// Dequantize a texture channel value [0..1] into normalized data space.
// QUANT_254 layers (updated GFS pipeline) encode byte = 1 + 254*(v-vmin)/range
// with byte 0 reserved for NoData, so the correct inverse is (byte-1)/254 —
// NOT byte/255. Using byte/255 introduces a ~0.4% systematic scale error
// (≈0.7°F on the temperature range, ≈100 m on visibility) and, worse for
// vector data, shifts the encoding's zero point: 0 m/s wind encodes to byte
// 128, which byte/255-based recentering reads as ~0.55 m/s of phantom wind
// everywhere the air is calm. Legacy pipelines (MRMS, satellite) keep the
// plain byte/255 mapping.
float dequant(float v) {
  #if QUANT_254 == 1
    return clamp((v * 255.0 - 1.0) / 254.0, 0.0, 1.0);
  #else
    return v;
  #endif
}

// Decode a raw normalized value [0..1] from an RGBA pixel.
float decodeValue(vec4 px) {
  #if DECODE_MODE == 1
    // Scalar from the configured channel. The COG worker packs band 1→R,
    // band 2→G, band 3→B, and this lets one packed RGB tile back multiple
    // logical layers (e.g. dewpoint/temperature/apparent_temp share a tile).
    #if BAND_CHANNEL == 1
      return dequant(px.g);
    #elif BAND_CHANNEL == 2
      return dequant(px.b);
    #else
      return dequant(px.r);
    #endif
  #elif DECODE_MODE == 2
    // U/V components recentered from [0..1] to [-1..+1]. Under QUANT_254 the
    // dequant() puts the encoding's true zero (byte 128) exactly at 0.5, so
    // calm air decodes to a genuine zero-magnitude vector.
    float u = (dequant(px.r) - 0.5) * 2.0;
    float v = (dequant(px.g) - 0.5) * 2.0;
    return clamp(sqrt(u * u + v * v), 0.0, 1.0);
  #elif DECODE_MODE == 3
    return (px.r * 255.0 * 65536.0 + px.g * 255.0 * 256.0 + px.b * 255.0) / 16777215.0;
  #else
    return dequant(px.r);
  #endif
}

#if SMOOTH >= 1
// Compute the final RGBA color at a single UV — category lookup + palette,
// no blur. Used as the inner building block for the color-space blur.
vec4 sampleColor(vec2 uv) {
  vec4 px0 = texture(u_tex0, uv);
  vec4 px1 = texture(u_tex1, uv);
  float v0 = decodeValue(px0);
  float v1 = decodeValue(px1);
  float v = mix(v0, v1, u_time);
  // The LUT has transparent at bytes 0 and 255, so no-data → alpha 0
  // automatically, no need to discard here.
  return texture(u_palette, vec2(clamp(v, 0.0, 1.0), 0.5));
}
#endif

#if SMOOTH == 2 || SMOOTH == 5
// CATEGORY-AWARE SHADER-SIDE BILINEAR SAMPLE.
//
// Why we can't use gl.LINEAR on the texture directly:
//   The byte encoding is non-linear — adjacent byte values represent unrelated
//   categories. Byte 0 (no precip) and byte 84 (heavy rain) are encoding
//   sentinels separated by a meaningful gap in PALETTE space, not data space.
//   A hardware bilinear blend of 0 and 84 produces 42 (which the LUT renders
//   as light rain that doesn't exist), and a blend of byte 255 (no-data
//   sentinel at coverage edges) with byte 0 produces ~127 (which lands in
//   the sleet range — that's why "pink lines" appear at radar coverage
//   borders when LINEAR is enabled).
//
// What this does instead:
//   Reads the 4 corner texels with texelFetch (no filtering — returns exact
//   stored bytes), excludes any corner that's a sentinel (0 or 255), then
//   bilinearly weights the surviving corners using the standard bilinear
//   weights but renormalized over only the valid corners. The result is a
//   smooth sub-texel-accurate sample that NEVER produces a value outside
//   the byte ranges that actually exist in the source frames.
//
// Returns vec2(byteValue, coverage):
//   - byteValue: the bilinear-blended byte in [0,1] space, or 0 if all
//     corners were sentinels (caller skips by checking coverage)
//   - coverage: 0..1, fraction of bilinear weight that landed on valid data.
//     Used by the caller to fade the kernel weight at coverage edges so
//     storms don't get an artificial "halo" where a tap straddles the data
//     boundary.
vec2 categoryBilinearByte(sampler2D tex, vec2 uv) {
  vec2 pos = uv * u_texSize - 0.5;
  vec2 floorPos = floor(pos);
  vec2 frac = pos - floorPos;

  // Clamp coords to the texture bounds — texelFetch with out-of-range
  // indices returns 0, which would falsely add a no-precip corner to the
  // average at the texture edge. We want EDGE_CLAMP behavior here.
  ivec2 maxIdx = ivec2(u_texSize) - ivec2(1);
  ivec2 p00 = clamp(ivec2(floorPos),               ivec2(0), maxIdx);
  ivec2 p10 = clamp(ivec2(floorPos) + ivec2(1, 0), ivec2(0), maxIdx);
  ivec2 p01 = clamp(ivec2(floorPos) + ivec2(0, 1), ivec2(0), maxIdx);
  ivec2 p11 = clamp(ivec2(floorPos) + ivec2(1, 1), ivec2(0), maxIdx);

  float v00 = texelFetch(tex, p00, 0).r;
  float v10 = texelFetch(tex, p10, 0).r;
  float v01 = texelFetch(tex, p01, 0).r;
  float v11 = texelFetch(tex, p11, 0).r;

  // Bilinear weights for the 4 corners
  float w00 = (1.0 - frac.x) * (1.0 - frac.y);
  float w10 =        frac.x  * (1.0 - frac.y);
  float w01 = (1.0 - frac.x) *        frac.y;
  float w11 =        frac.x  *        frac.y;

  // Validity mask: 1 if the corner is real precip data, 0 if sentinel.
  // Real data is strictly between byte 0 (no precip) and byte 255 (no data) —
  // i.e. v in (0, 1) exclusive on both ends in normalized space.
  float m00 = step(0.5/255.0, v00) * step(v00, 254.5/255.0);
  float m10 = step(0.5/255.0, v10) * step(v10, 254.5/255.0);
  float m01 = step(0.5/255.0, v01) * step(v01, 254.5/255.0);
  float m11 = step(0.5/255.0, v11) * step(v11, 254.5/255.0);

  // Effective weights (zero out invalid corners)
  float ew00 = w00 * m00;
  float ew10 = w10 * m10;
  float ew01 = w01 * m01;
  float ew11 = w11 * m11;
  float wSum = ew00 + ew10 + ew01 + ew11;

  if (wSum < 1e-4) return vec2(0.0, 0.0);

  // Renormalized bilinear sample over valid corners only
  float v = (v00 * ew00 + v10 * ew10 + v01 * ew01 + v11 * ew11) / wSum;

  // Coverage = fraction of full bilinear weight that landed on valid data
  // (already normalized because w00+w10+w01+w11 = 1)
  float coverage = ew00 + ew10 + ew01 + ew11;

  return vec2(v, coverage);
}

// Sentinel-safe bicubic source sample using B-spline weights. The 4x4
// neighborhood version of categoryBilinearByte. Used in place of bilinear at
// the source-data sampling layer to eliminate visible source-pixel
// boundaries at extreme zoom — bilinear produces flat output within a
// single source texel (every screen pixel inside that texel averages the
// same handful of neighbors), so when one source texel covers many screen
// pixels you can see the source grid. B-spline bicubic produces smoothly-
// varying output across an entire 4x4 source neighborhood, with continuous
// derivative, so intra-texel screen pixels get genuinely different values.
//
// B-spline weights are chosen specifically because they are all non-negative
// (unlike Catmull-Rom or Mitchell-Netravali, which have small negative
// lobes). With sentinel-based corner exclusion we can't risk negative
// weights producing out-of-range or wrong-category interpolated values.
// B-spline is technically an *approximating* spline (it does not pass
// through the source samples exactly), making the result a touch blurrier
// than Catmull-Rom — but here that blur is the goal, not a side effect.
//
// 16 texelFetch per call, ~4x the cost of the bilinear version. With our
// 29-tap kernel that's ~464 fetches per fragment; modern GPUs are fine
// with this for typical viewport sizes but the cost is noticeable.
vec2 categoryBicubicByte(sampler2D tex, vec2 uv) {
  vec2 pos = uv * u_texSize - 0.5;
  vec2 floorPos = floor(pos);
  vec2 frac = pos - floorPos;

  // B-spline cubic basis weights for fractional position t in [0,1].
  // Sums to 1, all non-negative.
  vec4 wx, wy;
  {
    float t = frac.x;
    float t2 = t * t;
    float t3 = t2 * t;
    wx.x = (1.0 - 3.0*t + 3.0*t2 - t3) / 6.0;
    wx.y = (4.0 - 6.0*t2 + 3.0*t3) / 6.0;
    wx.z = (1.0 + 3.0*t + 3.0*t2 - 3.0*t3) / 6.0;
    wx.w = t3 / 6.0;
  }
  {
    float t = frac.y;
    float t2 = t * t;
    float t3 = t2 * t;
    wy.x = (1.0 - 3.0*t + 3.0*t2 - t3) / 6.0;
    wy.y = (4.0 - 6.0*t2 + 3.0*t3) / 6.0;
    wy.z = (1.0 + 3.0*t + 3.0*t2 - 3.0*t3) / 6.0;
    wy.w = t3 / 6.0;
  }

  ivec2 maxIdx = ivec2(u_texSize) - ivec2(1);
  float vSum = 0.0;
  float coverage = 0.0;

  // 4x4 neighborhood: tap j,i ranges from -1 to +2 relative to floorPos.
  // Loop unrolled for shader compatibility with older drivers — also avoids
  // dynamic indexing on the weights vec4s.
  for (int j = 0; j < 4; j++) {
    for (int i = 0; i < 4; i++) {
      ivec2 p = clamp(ivec2(floorPos) + ivec2(i - 1, j - 1), ivec2(0), maxIdx);
      float v = texelFetch(tex, p, 0).r;
      float mask = step(0.5/255.0, v) * step(v, 254.5/255.0);
      float wxi = (i == 0) ? wx.x : (i == 1) ? wx.y : (i == 2) ? wx.z : wx.w;
      float wyj = (j == 0) ? wy.x : (j == 1) ? wy.y : (j == 2) ? wy.z : wy.w;
      float w = wxi * wyj * mask;
      vSum     += v * w;
      coverage += w;
    }
  }

  if (coverage < 1e-4) return vec2(0.0, 0.0);
  return vec2(vSum / coverage, coverage);
}
#endif

#if 0
// (Kept for reference — the unused helpers below were the previous attempt
// at color-space bilinear, but they sampled palette colors instead of byte
// values, which is what produced the rainbow-ring artifacts at category
// boundaries. The new categoryBilinearByte above replaces both of these
// with a sentinel-safe byte-space bilinear that the existing kernel can
// consume directly.)
vec4 bilinearColor(sampler2D tex, vec2 uv) {
  vec2 pos = uv * u_texSize - 0.5;
  vec2 floorPos = floor(pos);
  vec2 frac = pos - floorPos;

  ivec2 p00 = ivec2(floorPos);
  ivec2 p10 = p00 + ivec2(1, 0);
  ivec2 p01 = p00 + ivec2(0, 1);
  ivec2 p11 = p00 + ivec2(1, 1);

  float v00 = texelFetch(tex, p00, 0).r;
  float v10 = texelFetch(tex, p10, 0).r;
  float v01 = texelFetch(tex, p01, 0).r;
  float v11 = texelFetch(tex, p11, 0).r;

  vec4 c00 = texture(u_palette, vec2(clamp(v00, 0.0, 1.0), 0.5));
  vec4 c10 = texture(u_palette, vec2(clamp(v10, 0.0, 1.0), 0.5));
  vec4 c01 = texture(u_palette, vec2(clamp(v01, 0.0, 1.0), 0.5));
  vec4 c11 = texture(u_palette, vec2(clamp(v11, 0.0, 1.0), 0.5));

  // Premultiply alpha so transparent corners don't darken the RGB average.
  c00.rgb *= c00.a;  c10.rgb *= c10.a;
  c01.rgb *= c01.a;  c11.rgb *= c11.a;

  vec4 top    = mix(c00, c10, frac.x);
  vec4 bottom = mix(c01, c11, frac.x);
  vec4 result = mix(top, bottom, frac.y);

  if (result.a > 0.0) result.rgb /= result.a;
  return result;
}

// Run the full 29-tap Gaussian blur on a single frame texture.
// Returns the final post-threshold RGBA for that frame.
vec4 gaussianBlurFrame(sampler2D tex) {
  vec2 texel = 1.0 / u_texSize;

  vec2 offsets[29] = vec2[29](
    vec2( 0.0,  0.0),
    vec2( 1.2,  0.0), vec2(-1.2,  0.0),
    vec2( 0.0,  1.2), vec2( 0.0, -1.2),
    vec2( 0.85,  0.85), vec2(-0.85,  0.85),
    vec2( 0.85, -0.85), vec2(-0.85, -0.85),
    vec2( 2.8,  0.0), vec2(-2.8,  0.0),
    vec2( 0.0,  2.8), vec2( 0.0, -2.8),
    vec2( 2.0,  2.0), vec2(-2.0,  2.0),
    vec2( 2.0, -2.0), vec2(-2.0, -2.0),
    vec2( 5.0,  0.0), vec2(-5.0,  0.0),
    vec2( 0.0,  5.0), vec2( 0.0, -5.0),
    vec2( 3.5,  3.5), vec2(-3.5,  3.5),
    vec2( 3.5, -3.5), vec2(-3.5, -3.5),
    vec2( 4.6,  1.9), vec2(-4.6,  1.9),
    vec2( 4.6, -1.9), vec2(-4.6, -1.9)
  );
  float weights[29] = float[29](
    0.14,
    0.075, 0.075, 0.075, 0.075,
    0.055, 0.055, 0.055, 0.055,
    0.025, 0.025, 0.025, 0.025,
    0.020, 0.020, 0.020, 0.020,
    0.006, 0.006, 0.006, 0.006,
    0.005, 0.005, 0.005, 0.005,
    0.004, 0.004, 0.004, 0.004
  );

  vec4 accum = vec4(0.0);
  float wSum = 0.0;
  for (int i = 0; i < 29; i++) {
    vec4 c = bilinearColor(tex, v_uv + offsets[i] * texel);
    accum.rgb += c.rgb * c.a * weights[i];
    accum.a   += c.a          * weights[i];
    wSum += weights[i];
  }
  accum.a /= wSum;
  if (accum.a > 0.0) accum.rgb /= accum.a;
  else               accum.rgb = vec3(0.0);

  // Threshold + remap: drop speckle, keep legitimate light precip.
  if (accum.a < 0.05) accum.a = 0.0;
  else accum.a = smoothstep(0.05, 0.25, accum.a) * (0.6 + 0.4 * accum.a);

  return accum;
}
#endif

void main() {
  #if SMOOTH == 3
    // ── MRMS motion-warped pipeline ──
    //
    // Pre-computed optical flow field (u_flow) tells us, for each region of
    // the map, how precipitation moved from frame A to frame B. During the
    // time interpolation, instead of fading one frame into the other at a
    // fixed location, we SAMPLE each frame at a DISPLACED location along the
    // motion vector. This produces actual apparent motion — storms translate
    // across the map rather than ghost-fade.
    //
    // Forward-backward warping:
    //   For u_time = t in [0, 1]:
    //     sample_A_at = v_uv - flow * t          (where A's pixel would be at time t)
    //     sample_B_at = v_uv + flow * (1 - t)    (where B's pixel was at time t)
    //   Then blend the samples with smoothstep(u_time) as the weight.
    //
    // The motion field is a coarse grid (e.g., 64×42); bilinear filtering on
    // the u_flow texture gives us smoothly-varying flow per pixel.

    // Decode motion flow at this UV position
    vec4 flowPx = texture(u_flow, v_uv);
    // R,G in [0..1], centered at 127/255 = 0.498. Recenter to [-1..+1].
    vec2 flowNorm = vec2(flowPx.r, flowPx.g) * 2.0 - 1.0;
    float flowConf = flowPx.b;  // confidence in [0..1]

    // Convert from normalized units to UV-space displacement.
    // flowNorm * u_flowMaxDisp gives displacement in SOURCE PIXELS.
    // Divide by u_texSize to get UV-space offset.
    vec2 flowUV = flowNorm * u_flowMaxDisp / u_texSize;

    // Scale flow strength by confidence — low-confidence regions fall back
    // toward cross-fade (flowUV → 0), which is at least stable.
    flowUV *= flowConf;

    // Forward-backward warp: sample A from "earlier" direction, B from "later"
    vec2 uvA = v_uv - flowUV * u_time;
    vec2 uvB = v_uv + flowUV * (1.0 - u_time);

    // Clamp UVs to [0,1] so we don't wrap around the texture
    uvA = clamp(uvA, vec2(0.001), vec2(0.999));
    uvB = clamp(uvB, vec2(0.001), vec2(0.999));

    // Sample raw bytes (NEAREST filter on MRMS frame textures preserves
    // exact category codes — warping just displaces them, doesn't blend)
    float byteA = texture(u_tex0, uvA).r;
    float byteB = texture(u_tex1, uvB).r;

    // Temperature-style continuous blend of warped samples
    float byte = mix(byteA, byteB, u_time);

    // Coverage from warped samples (avoids ghost rain in empty regions)
    float covA = byteA > 0.0 ? 1.0 : 0.0;
    float covB = byteB > 0.0 ? 1.0 : 0.0;
    float cov  = mix(covA, covB, u_time);

    vec4 color = texture(u_palette, vec2(clamp(byte, 0.0, 1.0), 0.5));
    color.a *= cov;

    if (color.a < 0.01) discard;
  #elif SMOOTH == 5
    // ── HARDWARE BILINEAR + LUT ─────────────────────────────────────────────
    //
    // The texture is uploaded with gl.LINEAR (see buildFrameTexture
    // simplePrecipType branch), so texture(u_tex0, v_uv).r returns a byte
    // value that's already bilinearly interpolated between the four source
    // texels surrounding v_uv. The GPU does this in dedicated hardware —
    // there is nothing cheaper.
    //
    // Known minor artifact: at precip-type category boundaries (byte 84↔85
    // rain↔sleet, byte 169↔170 sleet↔snow) the hardware-bilinear-averaged
    // byte falls in the wrong category's range and renders with the wrong
    // category's hot colors. Visible as a thin yellow contour around sleet
    // and snow patches. Several attempts to fix this in-shader were tried
    // and none worked well; left as-is for now.

    float byte0 = texture(u_tex0, v_uv).r;
    float byte1 = texture(u_tex1, v_uv).r;
    float byte = mix(byte0, byte1, u_time);

    // Animation-aware noise-floor cut. Light-precip pixels at the storm
    // edge pulse during playback because they cross the LUT's narrow alpha
    // ramp every cycle (byte 2 → byte 0 → byte 2 → ...). Raising the cut
    // during animation suppresses pixels below the pulse zone, while a
    // static (paused) view keeps the standard threshold so drizzle stays
    // visible.
    float animCut = mix(0.0, 0.04, u_animating);
    if (byte < animCut) discard;

    vec4 color = texture(u_palette, vec2(clamp(byte, 0.0, 1.0), 0.5));
    if (color.a < 0.01) discard;

  #elif SMOOTH == 2
    // ── MRMS smooth-flow pipeline ──
    //
    // The goal: make MRMS animate as fluidly as Temperature does, where
    // mix(byte_A, byte_B, t) produces a continuous color transition.
    //
    // Temperature works because every byte is valid data and the palette is
    // smooth. MRMS breaks naively because:
    //   a) byte 0 = "no data", so mix(0, 50, 0.5) = 25 = fake "light rain"
    //      that exists in NEITHER frame — produces ghosts during transitions
    //   b) byte 84 vs 85 crosses rain/sleet with visually opposite colors
    //
    // Our approach:
    //   1. For each pixel, blur both frames' bytes with the 29-tap Gaussian
    //   2. If both blurred bytes are non-zero, blend them linearly — this
    //      gives smooth flow through intermediate bytes (the LUT is designed
    //      to handle byte-84→byte-85 gracefully via the transition zones
    //      in buildPrecipTypeLUT)
    //   3. If only one is non-zero, use that byte and fade alpha — this
    //      avoids the "ghost rain at half intensity" problem when precip
    //      appears or dissipates between frames
    //
    // This gives smooth time flow for pixels inside persistent precip masses,
    // and clean alpha fades at the edges where cells appear/dissipate.

    // ── CATEGORY-AWARE BLUR ─────────────────────────────────────────────────
    //
    // Simple Gaussian blur across category boundaries produces visible halos:
    // black voids between snow and sleet (coverage drops to zero), and red
    // rings around sleet (intermediate bytes fall into the high-rain range).
    //
    // Fix: classify each kernel tap into rain/sleet/snow based on its byte
    // range, accumulate per-category weighted intensity & presence, then pick
    // the DOMINANT category and build the output byte from that category's
    // mean intensity alone. This guarantees the displayed color comes from the
    // category with the most support in this pixel's neighborhood — no
    // cross-category bleed.
    //
    // Byte ranges (after /255 normalization):
    //   Rain  : 0.004..0.329  (bytes 1..84)
    //   Sleet : 0.333..0.663  (bytes 85..169)
    //   Snow  : 0.667..0.996  (bytes 170..254)

    // Texel size — multiplied by u_kernelScale so the blur stays roughly
    // constant in *screen* size as the user zooms in. Without this, the
    // kernel is fixed in *texel* size and the texel grid becomes visible at
    // high zoom because the blur covers a tiny patch of screen relative to
    // the size of one source pixel.
    vec2 texel = u_kernelScale / u_texSize;

    // Kernel rotation jitter. The 29-tap kernel has axis-aligned and
    // diagonal-aligned offsets; on stair-stepped source data with constant
    // taps per fragment, that geometry produces visible diamond/box patterns
    // at high zoom (every nearby fragment samples the same offsets and shows
    // the same shape). Per-fragment rotating the kernel by a small pseudo-
    // random angle scatters those samples across rotational orientations, so
    // the pattern dissolves into texture noise that the eye reads as natural.
    //
    // Same trick used by real-time graphics for soft shadows (PCF jitter)
    // and blue-noise sampling. The hash-based randomness is stable over
    // time at a given pixel, so the noise doesn't shimmer between frames.
    float jitterSeed = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
    float jitterAngle = jitterSeed * 6.2831853;  // 0..2π
    float ja_c = cos(jitterAngle);
    float ja_s = sin(jitterAngle);
    mat2 jitterRot = mat2(ja_c, -ja_s, ja_s, ja_c);

    // ── TIGHTENED KERNEL (testing crisper storm edges) ───────────────────
    // Same 29-tap structure as before but:
    //   - Outer ring radius pulled from 5.0 → 3.5 (and 4.6 → 3.1)
    //   - Middle ring pulled from 2.8 → 2.2 (axis), 2.0 → 1.6 (diag)
    //   - Inner ring kept at ~1.2 / 0.85 (small detail intact)
    //   - Center weight bumped 0.14 → 0.22
    //   - Outer rings down-weighted so total still ≈ 1.0
    // This narrows the effective sigma and concentrates energy near the
    // sample point. Storm cores should look crisper without losing
    // the temporal/noise smoothing the kernel provides.
    // To revert, restore the previous arrays (preserved in git history).
    vec2 offsets[29] = vec2[29](
      vec2( 0.0,  0.0),
      vec2( 1.2,  0.0), vec2(-1.2,  0.0),
      vec2( 0.0,  1.2), vec2( 0.0, -1.2),
      vec2( 0.85,  0.85), vec2(-0.85,  0.85),
      vec2( 0.85, -0.85), vec2(-0.85, -0.85),
      vec2( 2.2,  0.0), vec2(-2.2,  0.0),
      vec2( 0.0,  2.2), vec2( 0.0, -2.2),
      vec2( 1.6,  1.6), vec2(-1.6,  1.6),
      vec2( 1.6, -1.6), vec2(-1.6, -1.6),
      vec2( 3.5,  0.0), vec2(-3.5,  0.0),
      vec2( 0.0,  3.5), vec2( 0.0, -3.5),
      vec2( 2.5,  2.5), vec2(-2.5,  2.5),
      vec2( 2.5, -2.5), vec2(-2.5, -2.5),
      vec2( 3.1,  1.3), vec2(-3.1,  1.3),
      vec2( 3.1, -1.3), vec2(-3.1, -1.3)
    );
    float weights[29] = float[29](
      0.2523,
      0.0803, 0.0803, 0.0803, 0.0803,
      0.0516, 0.0516, 0.0516, 0.0516,
      0.0229, 0.0229, 0.0229, 0.0229,
      0.0206, 0.0206, 0.0206, 0.0206,
      0.0046, 0.0046, 0.0046, 0.0046,
      0.0034, 0.0034, 0.0034, 0.0034,
      0.0034, 0.0034, 0.0034, 0.0034
    );

    // Per-category accumulators for frame A
    float rainWeightA = 0.0, rainIntensityA = 0.0;
    float sleetWeightA = 0.0, sleetIntensityA = 0.0;
    float snowWeightA = 0.0, snowIntensityA = 0.0;
    // Per-category for frame B
    float rainWeightB = 0.0, rainIntensityB = 0.0;
    float sleetWeightB = 0.0, sleetIntensityB = 0.0;
    float snowWeightB = 0.0, snowIntensityB = 0.0;

    // Category bounds in [0,1] texture-value space (byte/255)
    const float RAIN_MAX  = 0.331;  // byte 84.4
    const float SLEET_MAX = 0.665;  // byte 169.5

    // Width of the soft category-boundary blend zone, in normalized texture
    // space. 0.008 ≈ 2 byte values on either side of the boundary. Wide
    // enough to break up the discrete classification steps that produced
    // visible "onion-layer" contour rings near the boundary; narrow enough
    // that >95% of pixels still classify cleanly into one category.
    const float CAT_BLEND = 0.008;

    for (int i = 0; i < 29; i++) {
      vec2 uv = v_uv + (jitterRot * offsets[i]) * texel;
      // TEST: bilinear sampling instead of bicubic to evaluate whether the
      // bicubic was the source of the blur softness we want to tune. The
      // bicubic version (categoryBicubicByte) is preserved above and can be
      // restored by reverting these two lines. Bilinear is cheaper (4 fetches
      // vs 16) and may give a crisper result if the downstream Gaussian is
      // doing enough of the smoothing already. If we see the "source grid"
      // become visible at extreme zoom, swap back to bicubic.
      vec2 sampleA = categoryBilinearByte(u_tex0, uv);
      vec2 sampleB = categoryBilinearByte(u_tex1, uv);
      float va = sampleA.x;
      float vb = sampleB.x;
      float w  = weights[i];
      float wA = w * sampleA.y;
      float wB = w * sampleB.y;

      // ── SOFT PER-TAP CATEGORY CLASSIFICATION ─────────────────────────────
      //
      // The earlier version put 100% of a tap's weight into a single category
      // bucket (rain/sleet/snow) based on a hard byte-range comparison.
      // That produced two visible artifacts:
      //
      //   1. "Onion ring" contour lines around storm cores. Within a single
      //      category, the within-category intensity changes smoothly with
      //      byte value, but at the category boundaries the kernel's
      //      classification flipped abruptly tap-to-tap, drawing a visible
      //      contour wherever the byte values straddled the boundary.
      //
      //   2. Hard ptype transition lines. A pixel in the middle of a real
      //      rain→sleet transition zone had ~80%+ of its kernel weight in
      //      one category, so the soft category blend (downstream) mostly
      //      showed the dominant color and the transition looked like a
      //      cliff instead of a gradient.
      //
      // The fix: split each tap's weight across categories proportionally
      // using smoothstep across each boundary, so a tap at byte 85 (right
      // on the rain/sleet edge) contributes ~50/50 to both buckets and the
      // downstream blend naturally produces the in-between color.
      //
      // Per-tap intensity within each category is still computed at the
      // tap's actual byte value — a tap at byte 85 contributes "max-rain
      // intensity" to the rain bucket and "min-sleet intensity" to the
      // sleet bucket, half-weight each. That's the correct mental model
      // for a pixel sitting right at the transition: heaviest possible
      // rain, lightest possible sleet, equal probability.

      // Frame A
      if (sampleA.y > 0.0) {
        // Soft-membership weights for each category at this tap's byte value.
        // pRain  = 1 well below RAIN_MAX,  fades to 0 across the boundary
        // pSleet = 1 between boundaries,   fades at both ends
        // pSnow  = 0 well below SLEET_MAX, fades to 1 across the boundary
        float t1 = smoothstep(RAIN_MAX  - CAT_BLEND, RAIN_MAX  + CAT_BLEND, va);
        float t2 = smoothstep(SLEET_MAX - CAT_BLEND, SLEET_MAX + CAT_BLEND, va);
        float pRain  = 1.0 - t1;
        float pSleet = t1 - t2;
        float pSnow  = t2;

        // Per-category intensities (each tap contributes its own intensity
        // to whichever buckets it's a member of). The clamps still limit
        // intensities to [0,1] within each category — a tap at byte 85
        // produces rain intensity ~1.0 and sleet intensity ~0.0, which is
        // exactly right for a tap sitting on the boundary.
        float rainI  = clamp((va - 0.004) / 0.325, 0.0, 1.0);
        float sleetI = clamp((va - 0.333) / 0.329, 0.0, 1.0);
        float snowI  = clamp((va - 0.667) / 0.329, 0.0, 1.0);

        rainWeightA    += wA * pRain;
        rainIntensityA += rainI  * wA * pRain;
        sleetWeightA   += wA * pSleet;
        sleetIntensityA+= sleetI * wA * pSleet;
        snowWeightA    += wA * pSnow;
        snowIntensityA += snowI  * wA * pSnow;
      }

      // Frame B
      if (sampleB.y > 0.0) {
        float t1 = smoothstep(RAIN_MAX  - CAT_BLEND, RAIN_MAX  + CAT_BLEND, vb);
        float t2 = smoothstep(SLEET_MAX - CAT_BLEND, SLEET_MAX + CAT_BLEND, vb);
        float pRain  = 1.0 - t1;
        float pSleet = t1 - t2;
        float pSnow  = t2;

        float rainI  = clamp((vb - 0.004) / 0.325, 0.0, 1.0);
        float sleetI = clamp((vb - 0.333) / 0.329, 0.0, 1.0);
        float snowI  = clamp((vb - 0.667) / 0.329, 0.0, 1.0);

        rainWeightB    += wB * pRain;
        rainIntensityB += rainI  * wB * pRain;
        sleetWeightB   += wB * pSleet;
        sleetIntensityB+= sleetI * wB * pSleet;
        snowWeightB    += wB * pSnow;
        snowIntensityB += snowI  * wB * pSnow;
      }
    }

    // Time-blend per-category weights and intensities. Each category is
    // essentially its own continuous layer that cross-fades independently.
    float rainW    = mix(rainWeightA,    rainWeightB,    u_time);
    float sleetW   = mix(sleetWeightA,   sleetWeightB,   u_time);
    float snowW    = mix(snowWeightA,    snowWeightB,    u_time);

    // Total coverage across all categories (for overall alpha fade)
    float totalW = rainW + sleetW + snowW;

    if (totalW < 0.01) discard;

    // Determine dominant category by weight. Use per-frame intensities where
    // that category has support; then time-blend the intensity too.
    // (Falling back to 0 when a category has no support on one side.)
    float rainInt  = (rainWeightA  > 0.0001 ? rainIntensityA  / rainWeightA  : 0.0);
    float sleetInt = (sleetWeightA > 0.0001 ? sleetIntensityA / sleetWeightA : 0.0);
    float snowInt  = (snowWeightA  > 0.0001 ? snowIntensityA  / snowWeightA  : 0.0);
    float rainIntB  = (rainWeightB  > 0.0001 ? rainIntensityB  / rainWeightB  : rainInt);
    float sleetIntB = (sleetWeightB > 0.0001 ? sleetIntensityB / sleetWeightB : sleetInt);
    float snowIntB  = (snowWeightB  > 0.0001 ? snowIntensityB  / snowWeightB  : snowInt);
    // If frame A has no support but frame B does, use B's intensity as the A value
    // (symmetric fallback, so a category fading in doesn't start from intensity 0)
    if (rainWeightA  < 0.0001 && rainWeightB  > 0.0001) rainInt  = rainIntB;
    if (sleetWeightA < 0.0001 && sleetWeightB > 0.0001) sleetInt = sleetIntB;
    if (snowWeightA  < 0.0001 && snowWeightB  > 0.0001) snowInt  = snowIntB;

    rainInt  = mix(rainInt,  rainIntB,  u_time);
    sleetInt = mix(sleetInt, sleetIntB, u_time);
    snowInt  = mix(snowInt,  snowIntB,  u_time);

    // ── SOFT CATEGORY BLEND ────────────────────────────────────────────────
    //
    // The previous version of this code picked a single dominant category by
    // weight and rendered only that category's color. That produces clean
    // results inside a uniform precip mass, but where two categories meet
    // (typical winter mixed-precip boundary: rain on the warm side, sleet
    // on the cold side, snow farther in), the dominant category flips
    // pixel-to-pixel based on tiny floating-point differences in kernel
    // weights — producing a speckled "salt-and-pepper" boundary that does
    // not match how the underlying data actually transitions.
    //
    // Real ptype-aware radar products handle this by *blending* the colors
    // of adjacent categories in the overlap zone. We do the same: for each
    // category that has nontrivial weight in this pixel's neighborhood, we
    // build its byte-space position, sample the LUT once, and combine the
    // results in color space using normalized weights.
    //
    // Sampling the LUT three times per fragment is cheap (one texture fetch
    // each, no branching, all in cache) and the result is a smooth gradient
    // through the boundary that matches the operational look of mixed-ptype
    // products like NWS dual-pol "HDR" and RadarScope's winter overlay.

    // Convert per-category intensities to byte-space LUT positions.
    float rainByte  = 0.004 + rainInt  * 0.325;  // bytes   1..84  → 0.004..0.329
    float sleetByte = 0.333 + sleetInt * 0.329;  // bytes  85..169 → 0.333..0.663
    float snowByte  = 0.667 + snowInt  * 0.329;  // bytes 170..254 → 0.667..0.996

    // Sample the LUT once per category. The LUT already encodes the
    // intensity-shaped alpha (light returns are semi-transparent, etc.).
    vec4 rainColor  = texture(u_palette, vec2(clamp(rainByte,  0.0, 1.0), 0.5));
    vec4 sleetColor = texture(u_palette, vec2(clamp(sleetByte, 0.0, 1.0), 0.5));
    vec4 snowColor  = texture(u_palette, vec2(clamp(snowByte,  0.0, 1.0), 0.5));

    // Weight each color by its category's coverage in the kernel, then
    // normalize. Premultiply alpha before mixing so a category with low
    // alpha at this intensity doesn't darken the blend.
    rainColor.rgb  *= rainColor.a;
    sleetColor.rgb *= sleetColor.a;
    snowColor.rgb  *= snowColor.a;

    float wSum = rainW + sleetW + snowW;  // == totalW (already > 0.01)
    vec4 color = vec4(0.0);
    color.rgb = (rainColor.rgb  * rainW
              +  sleetColor.rgb * sleetW
              +  snowColor.rgb  * snowW) / wSum;
    color.a   = (rainColor.a    * rainW
              +  sleetColor.a   * sleetW
              +  snowColor.a    * snowW) / wSum;

    // Un-premultiply RGB so downstream alpha attenuation (opacity, the final
    // blend equation) sees the normal un-premultiplied color. The very last
    // line of the shader re-premultiplies for the framebuffer blend.
    if (color.a > 0.0) color.rgb /= color.a;
    else               color.rgb = vec3(0.0);

    // Alpha fade based on total coverage — makes edges fade smoothly rather
    // than hard-cutting.
    color.a *= smoothstep(0.02, 0.25, totalW);

    if (color.a < 0.01) discard;
  #elif SMOOTH == 1
    // Lightweight color-space blur for continuous-data smoothed layers
    // (Euro radar, satellite). Same idea as the full pipeline but without
    // the bilinear-color step, which matters mostly for categorical data.
    vec2 texel = u_kernelScale / u_texSize;
    vec2 offsets[13] = vec2[13](
      vec2( 0.0,  0.0),
      vec2( 1.0,  0.0), vec2(-1.0,  0.0),
      vec2( 0.0,  1.0), vec2( 0.0, -1.0),
      vec2( 0.7,  0.7), vec2(-0.7,  0.7),
      vec2( 0.7, -0.7), vec2(-0.7, -0.7),
      vec2( 1.4,  0.0), vec2(-1.4,  0.0),
      vec2( 0.0,  1.4), vec2( 0.0, -1.4)
    );
    float weights[13] = float[13](
      0.20,
      0.10, 0.10, 0.10, 0.10,
      0.07, 0.07, 0.07, 0.07,
      0.03, 0.03, 0.03, 0.03
    );

    vec4 color = vec4(0.0);
    float wSum = 0.0;
    for (int i = 0; i < 13; i++) {
      vec4 c = sampleColor(v_uv + offsets[i] * texel);
      color.rgb += c.rgb * c.a * weights[i];
      color.a   += c.a          * weights[i];
      wSum += weights[i];
    }
    color.a   /= wSum;
    if (color.a > 0.0) color.rgb /= color.a;
    else               color.rgb = vec3(0.0);

    if (color.a < 0.01) discard;
  #else
    #if RGB_PRECIP == 1
    vec3 rgb0 = bicubicSample(u_tex0, v_uv, u_texSize);
    vec3 rgb1 = bicubicSample(u_tex1, v_uv, u_texSize);
    // Bilinear taps (hardware LINEAR) never ring/undershoot. Catmull-Rom
    // bicubic has a negative lobe that dips BELOW zero right next to a sharp
    // precip edge; clamping that dip to 0 (max(...,0)) created a fully
    // transparent gap between the solid fill and the faint outer ring — the
    // "transparent line / black gap" at blob edges. Flooring the bicubic
    // result with the bilinear value fills that dip with the smooth, always-
    // non-negative bilinear value, so the fill stays solid out to its edge
    // while bicubic still provides the crisper interior gradients.
    vec3 lin0 = texture(u_tex0, v_uv).rgb;
    vec3 lin1 = texture(u_tex1, v_uv).rgb;
    rgb0 = max(rgb0, lin0);
    rgb1 = max(rgb1, lin1);
    vec3 rgb = max(mix(rgb0, rgb1, u_time), vec3(0.0));

    // Total precip intensity at this pixel, summed across the three category
    // channels. Each source pixel is single-category (exactly one of R/G/B is
    // non-zero), but LINEAR/bicubic filtering blends neighbors at category
    // boundaries, so a transition pixel can carry a mix of two channels. We
    // keep totalW as the continuous intensity so edges fade smoothly to
    // zero instead of dropping out into a hard transparent seam.
    float totalW = rgb.r + rgb.g + rgb.b;
    // Discard floor: drop only the very faintest edge pixels. Kept low (the
    // data's real intensities run small) so we don't erase light precip — the
    // tight edge fade below does most of the cleanup. Below this, bicubic
    // falloff produces light-color-at-tiny-alpha pixels that composite over
    // the dark basemap as a dirty band; cutting just the bottom sliver helps.
    if (totalW < 0.004) discard;

    // ── Dominant-category coloring (no cross-palette halo) ──────────────────
    // The previous version remapped each channel into its own palette byte
    // range and averaged the three palette colors weighted by channel share.
    // At a boundary that averages, e.g., rain-red with sleet-pink and yields a
    // bright pink halo ring. Instead, pick the DOMINANT category for this
    // pixel and sample only that category's palette. The dominant category
    // changes exactly at the 50/50 line between two categories, so there's a
    // crisp hue switch with no third "blend" color — but because alpha is
    // taken from the palette (intensity-shaped) and only gently faded at the
    // very low end, the two categories meet cleanly and taper at the edges
    // without a halo or a hard transparent seam.
    //
    // intensity01 must be the DOMINANT channel's value, NOT the sum of all
    // three. At a category boundary bicubic blends two channels, and their
    // SUM spikes toward 1.0 even though neither category is actually intense
    // there — sampling the palette at that inflated intensity lands on the
    // dark HIGH-intensity end (sleet's maroon, snow's dark blue), producing
    // the dark band/ring seen along every boundary. The dominant channel
    // alone reflects the winning category's true intensity, so the palette is
    // sampled at the right (lighter, low-intensity) entry at edges.
    float cMax = max(rgb.r, max(rgb.g, rgb.b));

    // Seam-aware intensity. At a genuine boundary between two precip types,
    // both channels are present and each is roughly half-decayed, so the
    // DOMINANT channel (cMax) dips to a local minimum right on the meeting
    // line — even though there's clearly precip there (the SUM, totalW, stays
    // high). Sampling the palette at that dipped cMax lands on the category's
    // transparent low-intensity end, leaving a thin TRANSPARENT SEAM between
    // two touching precip masses (sleet↔snow, rain↔sleet).
    //
    // Fix: floor the intensity using totalW where two categories overlap. The
    // "secondary" coverage = totalW - cMax is ~0 in a single-category interior
    // or at an isolated blob edge (only one channel present), but rises toward
    // cMax at a 50/50 seam. We lift intensity by a FRACTION of that secondary
    // coverage — enough to clear the transparent low-intensity end so the two
    // masses connect, but NOT the full sum (cMax + secondary == totalW), which
    // would re-create the original dark boundary band by sampling the palette's
    // dark HIGH-intensity end at every seam. SEAM_LIFT tunes how much: 0 = old
    // behavior (transparent seam), 1 = full sum (dark band). A small lift just
    // clears the fade's transparent zone.
    //
    // Isolated edges are unaffected: secondary ≈ 0 there, so intensity ≈ cMax
    // and the ring-suppressing low-alpha fade still applies.
    const float SEAM_LIFT = 0.5;
    float secondary = max(totalW - cMax, 0.0);
    float intensity01 = clamp(cMax + secondary * SEAM_LIFT, 0.0, 1.0);

    // Dominant-category coloring with anti-steal margins. Pick the category
    // whose channel is largest, but require an UPPER category (sleet over
    // snow, rain over sleet) to CLEARLY beat the one below before it wins.
    // Without this, at a snow blob's rim the snow channel (B) falls off and
    // the bilinear floor lifts a faint sleet channel (G); a hair of G > B then
    // misclassifies the rim as sleet → the thin PINK RING around snow. The
    // 1.25× margin means sleet only claims a pixel where it genuinely
    // dominates snow, so snow keeps its own rim and the pink ring disappears.
    float palByte;
    if (rgb.r >= rgb.g * 1.25 && rgb.r >= rgb.b * 1.25) {
      // Rain clearly dominant → rain palette range (bytes 1..84).
      palByte = (intensity01 * 83.0 + 1.0) / 255.0;
    } else if (rgb.g >= rgb.b * 1.25 && rgb.g >= rgb.r) {
      // Sleet clearly beats snow (and ≥ rain) → sleet range (bytes 85..169).
      palByte = (intensity01 * 84.0 + 85.0) / 255.0;
    } else if (rgb.b > 0.0) {
      // Snow present and sleet did NOT clearly dominate it → snow range
      // (bytes 170..254). This is the rim case: a faint sleet lift that's
      // within 1.25× of the snow channel does NOT steal the pixel, so the
      // snow rim stays white instead of picking up a pink ring.
      palByte = (intensity01 * 84.0 + 170.0) / 255.0;
    } else {
      // No snow channel at all → sleet is the largest remaining.
      palByte = (intensity01 * 84.0 + 85.0) / 255.0;
    }

    vec4 color = texture(u_palette, vec2(palByte, 0.5));
    // Alpha: TIGHT feathered edge. The dark halo we just diagnosed was light
    // palette color at PARTIAL alpha compositing over the dark basemap. A wide
    // fade made that semi-transparent zone many pixels deep → visible dark
    // ring. A NARROW fade keeps a 1–2px feather (the soft edge look) while the
    // semi-transparent zone is too thin to read as a halo. Palette colors at
    // the low end are now light (snow near-white, sleet light magenta), so the
    // thin feather tints toward the basemap only very slightly.
    color.a *= smoothstep(0.004, 0.014, totalW);
    #else
    vec4 px0 = texture(u_tex0, v_uv);
    vec4 px1 = texture(u_tex1, v_uv);
    float v0 = decodeValue(px0);
    float v1 = decodeValue(px1);
    #if DISCARD_ZERO == 1
    if (v0 <= 0.0 && v1 <= 0.0) discard;
    #endif
    float v = mix(v0, v1, u_time);
    vec4 color = texture(u_palette, vec2(clamp(v, 0.0, 1.0), 0.5));
    #endif
  #endif

  color.a *= u_opacity;
  if (color.a < 0.01) discard;
  // Premultiply alpha to match the GL blend func gl.blendFunc(ONE, ONE_MINUS_SRC_ALPHA).
  // Without this, layers whose palette uses pure-white pixels with partial alpha
  // (e.g. cloud cover, satellite) blow out: source RGB enters the framebuffer at
  // its un-multiplied strength while the destination is only attenuated by
  // (1 - alpha), making partially-translucent pixels render as effectively-opaque
  // white. Multiplying RGB by alpha here matches the contract the blend equation
  // expects.
  outColor = vec4(color.rgb * color.a, color.a);
}`;

// ─── Three-pass FBO pipeline (multiPassSmooth) ────────────────────────────────
//
// Replaces the single-pass 29-tap kernel for precip-type layers when
// LayerConfig.multiPassSmooth is set. Three sequential passes:
//
//   Pass 1 (categorize):   source TIFs → FBO_A
//     For each fragment, read both source frames with shader-side bilinear
//     (sentinel-safe), classify each into rain/sleet/snow with soft membership,
//     time-blend per category, encode the result as RGBA where each channel
//     is a continuous quantity that's correct under hardware bilinear:
//       R = rain_weight  * rain_intensity
//       G = sleet_weight * sleet_intensity
//       B = snow_weight  * snow_intensity
//       A = total coverage (excluding sentinels)
//
//   Pass 2 (H blur):       FBO_A → FBO_B
//     Sample pass-1 output with hardware bilinear, accumulate a 1D Gaussian
//     horizontally. Bilinear is now safe because the per-channel encoding is
//     continuous (no byte-range categories to discontinuously cross).
//
//   Pass 3 (V blur + colorize):  FBO_B → map's framebuffer
//     Sample pass-2 output bilinearly, accumulate the vertical 1D Gaussian,
//     decode to per-category weight+intensity (channel / A), look up the
//     three palettes, blend by weight, output premultiplied RGBA.
//
// Why this fixes the boxy-contour problem:
//   The single-pass kernel uses 29 NEAREST samples per fragment. At high
//   zoom, each tap snaps to a discrete source texel and contributes a sharp
//   step function to the output. The 29 step functions superimposed produce
//   visible axis-aligned contour boxes — the kernel's "footprint signature"
//   embossed onto every isovalue boundary in the source data.
//
//   The three-pass version reads from the *previous pass's* output, which is
//   already a continuous field. Hardware bilinear filtering on that field is
//   safe (no category boundaries to skip across) and produces genuinely smooth
//   gradients at every tap. The kernel signature dissolves because the input
//   to the blur isn't stair-stepped anymore.

// Pass 1 — categorize source TIFs, look up per-category palette color, and
// write a premultiplied (color × weight) encoding that can be safely blurred.
//
// Multi-frame temporal smoothing (B5):
//   Instead of cross-fading just between the two surrounding frames (N and
//   N+1), we sample FOUR consecutive frames (N-1, N, N+1, N+2) and blend
//   them with a bell-curve weighted by distance from the current playback
//   time. This produces a "temporal motion blur" — moving cells appear with
//   faint trailing edges in their direction of travel and leading edges in
//   their direction of motion, which the eye reads as motion even though no
//   actual pixel displacement occurred.
//
// Output encoding (dual-FBO, MRT):
//   Pass 1 writes TWO render targets so that downstream blur passes smooth
//   intensities WITHIN each category (giving smooth gradients like the
//   single-pass kernel) rather than smoothing finished colors (which baked
//   the palette lookup in too early and produced visibly chunky cell edges
//   at intensity gradients). Each FBO is RGBA8.
//
//   FBO_A (intensities premultiplied by their weights):
//     R = rain_weight  × rain_intensity
//     G = sleet_weight × sleet_intensity
//     B = snow_weight  × snow_intensity
//     A = (unused; reserved)
//
//   FBO_B (per-category weights):
//     R = rain_weight
//     G = sleet_weight
//     B = snow_weight
//     A = total weight (= R+G+B; precomputed for convenience in pass 4)
//
//   In pass 4 we recover intensity_k = FBO_A.k / FBO_B.k (with a guard for
//   FBO_B.k → 0). All channels are linear quantities so they bilinearly
//   filter and sum-blur correctly.
const PASS1_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_texPrev;   // frame N-1
uniform sampler2D u_tex0;      // frame N (the canonical "current" frame)
uniform sampler2D u_tex1;      // frame N+1
uniform sampler2D u_texNext;   // frame N+2
uniform float u_time;          // fractional position in [0,1] between tex0 and tex1
uniform vec2 u_texSize;
uniform float u_temporalKernel; // half-width of the temporal triangle window

in vec2 v_uv;
layout(location = 0) out vec4 outIntensities;
layout(location = 1) out vec4 outWeights;

// Sentinel-safe shader-side bilinear sample. Returns vec2(byteValue, coverage);
// coverage is the fraction of bilinear weight that landed on valid (non-
// sentinel) data. Sentinels (byte 0 = no precip, byte 255 = no data) are
// excluded entirely so a bilinear average never produces a fake mid-range
// value at a coverage edge.
vec2 categoryBilinearByte(sampler2D tex, vec2 uv) {
  vec2 pos = uv * u_texSize - 0.5;
  vec2 floorPos = floor(pos);
  vec2 frac = pos - floorPos;

  ivec2 maxIdx = ivec2(u_texSize) - ivec2(1);
  ivec2 p00 = clamp(ivec2(floorPos),               ivec2(0), maxIdx);
  ivec2 p10 = clamp(ivec2(floorPos) + ivec2(1, 0), ivec2(0), maxIdx);
  ivec2 p01 = clamp(ivec2(floorPos) + ivec2(0, 1), ivec2(0), maxIdx);
  ivec2 p11 = clamp(ivec2(floorPos) + ivec2(1, 1), ivec2(0), maxIdx);

  float v00 = texelFetch(tex, p00, 0).r;
  float v10 = texelFetch(tex, p10, 0).r;
  float v01 = texelFetch(tex, p01, 0).r;
  float v11 = texelFetch(tex, p11, 0).r;

  float w00 = (1.0 - frac.x) * (1.0 - frac.y);
  float w10 =        frac.x  * (1.0 - frac.y);
  float w01 = (1.0 - frac.x) *        frac.y;
  float w11 =        frac.x  *        frac.y;

  float m00 = step(0.5/255.0, v00) * step(v00, 254.5/255.0);
  float m10 = step(0.5/255.0, v10) * step(v10, 254.5/255.0);
  float m01 = step(0.5/255.0, v01) * step(v01, 254.5/255.0);
  float m11 = step(0.5/255.0, v11) * step(v11, 254.5/255.0);

  float ew00 = w00 * m00, ew10 = w10 * m10, ew01 = w01 * m01, ew11 = w11 * m11;
  float wSum = ew00 + ew10 + ew01 + ew11;
  if (wSum < 1e-4) return vec2(0.0, 0.0);
  float v = (v00*ew00 + v10*ew10 + v01*ew01 + v11*ew11) / wSum;
  return vec2(v, ew00 + ew10 + ew01 + ew11);
}

// Decompose a single byte value into per-category (weight, intensity) pairs.
// Soft-membership smoothstep across the rain/sleet/snow boundaries means a
// tap right at byte 84/85 contributes ~50% to rain and ~50% to sleet, which
// gives smooth color blending in pass 4 instead of a hard category snap.
struct CatContribution {
  vec3 weights;     // (rainW, sleetW, snowW), sum = 1.0 for valid data
  vec3 intensities; // within-category positions (0..1), regardless of weight
};
CatContribution byteToCategories(float v) {
  const float RAIN_MAX  = 0.331;
  const float SLEET_MAX = 0.665;
  const float CAT_BLEND = 0.008;

  float t1 = smoothstep(RAIN_MAX  - CAT_BLEND, RAIN_MAX  + CAT_BLEND, v);
  float t2 = smoothstep(SLEET_MAX - CAT_BLEND, SLEET_MAX + CAT_BLEND, v);
  CatContribution c;
  c.weights.x = 1.0 - t1;            // rain
  c.weights.y = t1 - t2;             // sleet
  c.weights.z = t2;                  // snow
  c.intensities.x = clamp((v - 0.004) / 0.325, 0.0, 1.0);
  c.intensities.y = clamp((v - 0.333) / 0.329, 0.0, 1.0);
  c.intensities.z = clamp((v - 0.667) / 0.329, 0.0, 1.0);
  return c;
}

// Accumulate one frame's contribution into the running per-category sums.
// 'coverage' is the bilinear-sample coverage (0..1) — the fraction of the
// 2x2 source neighborhood that wasn't a sentinel byte. 'frameWeight' is
// the temporal-kernel weight for this frame in the multi-frame blend.
void accumulate(
  inout vec3 sumIntensities, inout vec3 sumWeights,
  float byteVal, float coverage, float frameWeight
) {
  if (coverage <= 0.0 || frameWeight <= 0.0) return;
  CatContribution c = byteToCategories(byteVal);
  // Effective per-category weight = membership x coverage x frameWeight.
  vec3 effW = c.weights * coverage * frameWeight;
  sumWeights    += effW;
  sumIntensities += effW * c.intensities;
}

void main() {
  if (v_uv.x < 0.0 || v_uv.x > 1.0 || v_uv.y < 0.0 || v_uv.y > 1.0) {
    outIntensities = vec4(0.0);
    outWeights     = vec4(0.0);
    return;
  }

  // Bilinear-safe samples from each of the four temporal neighbors.
  vec2 sP = categoryBilinearByte(u_texPrev, v_uv);  // frame N-1
  vec2 s0 = categoryBilinearByte(u_tex0,    v_uv);  // frame N
  vec2 s1 = categoryBilinearByte(u_tex1,    v_uv);  // frame N+1
  vec2 sN = categoryBilinearByte(u_texNext, v_uv);  // frame N+2

  // Triangle-kernel temporal weights — same as before. With u_temporalKernel
  // = 1.0 the prev/next contributions are zero and this reduces to standard
  // cross-fade between the central pair.
  float kw = max(u_temporalKernel, 0.001);
  float wP = max(0.0, 1.0 - (1.0 + u_time)       / kw);
  float w0 = max(0.0, 1.0 -  u_time              / kw);
  float w1 = max(0.0, 1.0 - (1.0 - u_time)       / kw);
  float wN = max(0.0, 1.0 - (2.0 - u_time)       / kw);
  float frameWSum = max(wP + w0 + w1 + wN, 1e-6);
  wP /= frameWSum; w0 /= frameWSum; w1 /= frameWSum; wN /= frameWSum;

  // Per-category accumulators.
  vec3 sumI = vec3(0.0);   // per-category weighted intensity (rain, sleet, snow)
  vec3 sumW = vec3(0.0);   // per-category weight

  accumulate(sumI, sumW, sP.x, sP.y, wP);
  accumulate(sumI, sumW, s0.x, s0.y, w0);
  accumulate(sumI, sumW, s1.x, s1.y, w1);
  accumulate(sumI, sumW, sN.x, sN.y, wN);

  outIntensities = vec4(sumI, 0.0);
  outWeights     = vec4(sumW, sumW.x + sumW.y + sumW.z);
}`;

// Shared 1D Gaussian blur. Reads both intensity and weight FBOs and blurs
// them independently, writing to two new FBOs via MRT. Same shader runs for
// the H pass (u_blurDir = (1,0)) and V pass (u_blurDir = (0,1)). Hardware
// bilinear filtering on the input FBOs is now safe because pass 1 wrote
// continuous quantities (no category boundaries to skip across).
//
// Why we sample the FBO with gl_FragCoord rather than v_uv:
//   v_uv is unit-quad space [0,1]² across the bbox geometry. The FBO was
//   written in screen-space (bbox geometry projected through u_mvp). Reading
//   the FBO at v_uv would map the bbox onto the FBO's [0,1]² range, but the
//   FBO is canvas-sized and the bbox occupies only a portion of it. We need
//   gl_FragCoord.xy / canvas_size to read the same screen pixel that pass 1
//   wrote.
const BLUR_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_inputI;    // intensity FBO from previous stage
uniform sampler2D u_inputW;    // weight FBO from previous stage
uniform vec2 u_pixelSize;      // 1 / canvas dimensions
uniform vec2 u_blurDir;        // (1,0) horizontal or (0,1) vertical
uniform float u_blurScale;     // multiplier on tap spacing (zoom-adaptive)

in vec2 v_uv;
layout(location = 0) out vec4 outI;
layout(location = 1) out vec4 outW;

const float W0 = 0.16210282;
const float W1 = 0.15064077;
const float W2 = 0.12136734;
const float W3 = 0.08504300;
const float W4 = 0.05172650;
const float W5 = 0.02728310;

void main() {
  vec2 fbUV = gl_FragCoord.xy * u_pixelSize;
  vec2 stp = u_blurDir * u_pixelSize * u_blurScale;

  vec4 sumI = vec4(0.0);
  vec4 sumW = vec4(0.0);

  // 11-tap Gaussian, both FBOs blurred in lock-step. Sampling 22 textures
  // per fragment but they're all in cache after the first pair.
  sumI += texture(u_inputI, fbUV) * W0;
  sumW += texture(u_inputW, fbUV) * W0;
  sumI += texture(u_inputI, fbUV + stp * 1.0) * W1;
  sumW += texture(u_inputW, fbUV + stp * 1.0) * W1;
  sumI += texture(u_inputI, fbUV - stp * 1.0) * W1;
  sumW += texture(u_inputW, fbUV - stp * 1.0) * W1;
  sumI += texture(u_inputI, fbUV + stp * 2.0) * W2;
  sumW += texture(u_inputW, fbUV + stp * 2.0) * W2;
  sumI += texture(u_inputI, fbUV - stp * 2.0) * W2;
  sumW += texture(u_inputW, fbUV - stp * 2.0) * W2;
  sumI += texture(u_inputI, fbUV + stp * 3.0) * W3;
  sumW += texture(u_inputW, fbUV + stp * 3.0) * W3;
  sumI += texture(u_inputI, fbUV - stp * 3.0) * W3;
  sumW += texture(u_inputW, fbUV - stp * 3.0) * W3;
  sumI += texture(u_inputI, fbUV + stp * 4.0) * W4;
  sumW += texture(u_inputW, fbUV + stp * 4.0) * W4;
  sumI += texture(u_inputI, fbUV - stp * 4.0) * W4;
  sumW += texture(u_inputW, fbUV - stp * 4.0) * W4;
  sumI += texture(u_inputI, fbUV + stp * 5.0) * W5;
  sumW += texture(u_inputW, fbUV + stp * 5.0) * W5;
  sumI += texture(u_inputI, fbUV - stp * 5.0) * W5;
  sumW += texture(u_inputW, fbUV - stp * 5.0) * W5;

  outI = sumI;
  outW = sumW;
}`;

// Pass 4 — read the smoothed intensity and weight FBOs, recover per-category
// (weight, intensity) pairs, look up the palette three times (once per
// category), blend the colors by category fraction, apply opacity, output
// premultiplied RGBA to the map's framebuffer.
//
// This is where the visual quality wins for MRMS came from. The single-pass
// kernel does the same thing inline — accumulate per-category weights and
// weighted intensities across taps, then resolve to color at the end. The
// previous multipass version did per-pixel palette lookup in pass 1 and then
// blurred the resulting colors, which produced visibly chunky edges where
// intensity gradients met low/zero coverage. Splitting intensity from color
// and blurring intensities-then-colorizing gives smooth gradients matching
// the single-pass kernel's appearance.
const COLORIZE_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp sampler2D;

uniform sampler2D u_inputI;    // smoothed (rainW*rainI, sleetW*sleetI, snowW*snowI)
uniform sampler2D u_inputW;    // smoothed (rainW, sleetW, snowW, totalW)
uniform sampler2D u_palette;
uniform vec2 u_pixelSize;
uniform float u_opacity;

in vec2 v_uv;
out vec4 outColor;

void main() {
  vec2 fbUV = gl_FragCoord.xy * u_pixelSize;
  vec4 packedI = texture(u_inputI, fbUV);
  vec4 packedW = texture(u_inputW, fbUV);

  float totalW = packedW.a;
  if (totalW < 0.01) discard;

  // Recover per-category (weight, intensity). Intensity = sumI_k / sumW_k
  // when sumW_k > 0; otherwise the category contributes nothing.
  vec3 catW = packedW.rgb;
  vec3 catI;
  catI.x = (catW.x > 1e-4) ? clamp(packedI.r / catW.x, 0.0, 1.0) : 0.0;
  catI.y = (catW.y > 1e-4) ? clamp(packedI.g / catW.y, 0.0, 1.0) : 0.0;
  catI.z = (catW.z > 1e-4) ? clamp(packedI.b / catW.z, 0.0, 1.0) : 0.0;

  // Map per-category intensities to byte-space LUT positions and look up
  // the three palette colors. cR/cS/cN come back as straight (un-premult)
  // RGBA — RGB is the displayed color, A is the palette's per-stop alpha.
  float rainByte  = 0.004 + catI.x * 0.325;
  float sleetByte = 0.333 + catI.y * 0.329;
  float snowByte  = 0.667 + catI.z * 0.329;
  vec4 cR = texture(u_palette, vec2(clamp(rainByte,  0.0, 1.0), 0.5));
  vec4 cS = texture(u_palette, vec2(clamp(sleetByte, 0.0, 1.0), 0.5));
  vec4 cN = texture(u_palette, vec2(clamp(snowByte,  0.0, 1.0), 0.5));

  // Per-pixel category fractions. fractions sum to 1 wherever totalW > 0.
  vec3 fractions = catW / max(totalW, 1e-6);

  // Color blend: weighted average of straight RGBs by fraction.
  vec3 blendedRGB = cR.rgb * fractions.x + cS.rgb * fractions.y + cN.rgb * fractions.z;

  // Alpha blend: weighted average of palette alphas by fraction. This is the
  // "inherent" alpha of the blended color before edge-fade and opacity. Then
  // apply the soft edge fade based on TOTAL coverage so storm-edge halos
  // taper smoothly, and finally apply layer opacity.
  float blendedA = cR.a * fractions.x + cS.a * fractions.y + cN.a * fractions.z;
  float finalA = blendedA * smoothstep(0.02, 0.25, totalW) * u_opacity;
  if (finalA < 0.01) discard;

  // Output premultiplied for the gl.blendFunc(ONE, ONE_MINUS_SRC_ALPHA)
  // target. RGB is the straight blended color × final alpha.
  outColor = vec4(blendedRGB * finalA, finalA);
}`;

// ─── WebGL helpers ──────────────────────────────────────────────────────────

type AnyGL = WebGL2RenderingContext | WebGLRenderingContext;

function isWebGL2(gl: AnyGL): gl is WebGL2RenderingContext {
  // Feature-test for a WebGL2-only method; cheaper than instanceof checks
  // that can fail across realms.
  return typeof (gl as WebGL2RenderingContext).createVertexArray === "function";
}

function compileShader(gl: AnyGL, type: number, src: string, name: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error(`createShader(${name}) returned null`);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) || "(no log)";
    console.error(`[WeatherRasterLayer] ${name} compile failed:\n${log}\n\nSource:\n${src}`);
    throw new Error(`${name} compile failed: ${log}`);
  }
  return sh;
}

function linkProgram(gl: AnyGL, vs: WebGLShader, fs: WebGLShader): WebGLProgram {
  const prog = gl.createProgram();
  if (!prog) throw new Error("createProgram returned null");
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(prog) || "(no log)";
    console.error(`[WeatherRasterLayer] program link failed:\n${log}`);
    throw new Error(`program link failed: ${log}`);
  }
  return prog;
}

function makeTexture(
  gl: AnyGL,
  data: Uint8Array,
  width: number,
  height: number,
  linearFilter: boolean,
  channels: 1 | 4 = 4,
): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("createTexture returned null");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  if (channels === 1) {
    // Single-channel R8 — 4× smaller GPU footprint than RGBA8 for single-band
    // data (radar, satellite). The shader reads .r as before, but the other
    // three channels don't exist on the GPU side either, so memory & cache
    // bandwidth are saved. WebGL2 required (gl.R8 is unavailable in WebGL1).
    //
    // UNPACK_ALIGNMENT defaults to 4. Single-channel rows aren't necessarily
    // 4-aligned (odd widths produce rows whose byte count isn't a multiple
    // of 4), so set it to 1 before the upload and restore after.
    const gl2 = gl as WebGL2RenderingContext;
    const prevAlign = gl2.getParameter(gl2.UNPACK_ALIGNMENT);
    gl2.pixelStorei(gl2.UNPACK_ALIGNMENT, 1);
    gl2.texImage2D(gl2.TEXTURE_2D, 0, gl2.R8, width, height, 0, gl2.RED, gl2.UNSIGNED_BYTE, data);
    gl2.pixelStorei(gl2.UNPACK_ALIGNMENT, prevAlign);
  } else {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  }
  const filter = linearFilter ? gl.LINEAR : gl.NEAREST;
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return tex;
}

// Build a tessellated unit-quad: (N+1)² vertices in unit-quad space [0,1]²,
// laid out as a regular lattice, wired into 2·N² triangles. UNSIGNED_SHORT
// indices fit (N+1)² up to 65536 vertices, i.e. N ≤ 254 — well above what
// we need (N=64 → 4225 verts).
function buildGridGeometry(n: number): { vertices: Float32Array; indices: Uint16Array } {
  const vw = n + 1; // vertices per row
  const vertices = new Float32Array(vw * vw * 2);
  let vi = 0;
  for (let y = 0; y < vw; y++) {
    for (let x = 0; x < vw; x++) {
      vertices[vi++] = x / n;
      vertices[vi++] = y / n;
    }
  }
  const indices = new Uint16Array(n * n * 6);
  let ii = 0;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const tl = y * vw + x;
      const tr = tl + 1;
      const bl = tl + vw;
      const br = bl + 1;
      // Two triangles per cell: (tl, tr, bl) and (tr, br, bl).
      indices[ii++] = tl; indices[ii++] = tr; indices[ii++] = bl;
      indices[ii++] = tr; indices[ii++] = br; indices[ii++] = bl;
    }
  }
  return { vertices, indices };
}

// ─── Public types ───────────────────────────────────────────────────────────

export interface WeatherFrame {
  /** Raw pixel data. Length is width*height for channels=1, width*height*4
   * for channels=4. Single-channel frames upload as gl.R8 textures; multi-
   * channel as gl.RGBA8. Most radar/satellite layers are single-channel. */
  rawData: Uint8Array;
  channels: 1 | 4;
  width: number;
  height: number;
  bbox: number[];       // [minLng, minLat, maxLng, maxLat] of the rendered window
  noData: number | null;
}

interface WeatherRasterLayerOptions {
  id: string;
  config: LayerConfig;
  opacity?: number;
}

// ─── Main class ─────────────────────────────────────────────────────────────

// How many segments the raster quad is subdivided into along each axis. The
// grid has (N+1)² vertices and 2·N² triangles. With N=64 that's 4225 verts
// and 8192 triangles — trivial for a modern GPU and enough resolution that
// even at maximum tilt (60°) the perspective distortion across each cell is
// less than a screen pixel.
const SUBDIVISION_N = 64;

export class WeatherRasterLayer implements MaplibreCustomLayer {
  // ── CustomLayerInterface required fields ──
  readonly id: string;
  readonly type = "custom" as const;
  readonly renderingMode = "2d" as const;

  private map: MaplibreMap | null = null;
  private config: LayerConfig;
  // GL context is supplied by MapLibre via onAdd. Until then it's null and
  // any setFrames/setOpacity/setTime calls just buffer state for later.
  private gl: WebGL2RenderingContext | WebGLRenderingContext | null = null;

  private program: WebGLProgram | null = null;
  private vao: WebGLVertexArrayObject | null = null;
  private vertexBuffer: WebGLBuffer | null = null;
  private indexBuffer: WebGLBuffer | null = null;
  private indexCount = 0;

  private tex0: WebGLTexture | null = null;
  private tex1: WebGLTexture | null = null;
  // Optional adjacent-frame textures used by the multiPassSmooth pipeline's
  // temporal blur. texPrev = frame N-1, texNext = frame N+2. When unavailable
  // (start/end of timeline, frame not in cache, or layer doesn't use temporal
  // blur), these stay null and the renderer binds tex0 / tex1 in their place
  // — which makes the corresponding kernel weight contribute the same content
  // as the central frame, gracefully reducing to standard cross-fade.
  private texPrev: WebGLTexture | null = null;
  private texNext: WebGLTexture | null = null;
  // ─── Frame→texture cache ──────────────────────────────────────────────────
  // Single owner of every frame texture. tex0/tex1/texPrev/texNext above are
  // now BORROWED references into this cache — they never own a texture and
  // are never deleted individually. This replaces the old slot-ownership
  // model (shift-reuse + scattered deleteTexture calls), which had a latent
  // use-after-delete: a single-frame pair (frame0 === frame1, produced by the
  // `f0 ?? f1` hole fallback) made the shift branch ALIAS tex0 = tex1; the
  // next pair change deleted one slot's texture while the other slot still
  // referenced it, and the following render bound a deleted object
  // ("WebGL: INVALID_OPERATION: bindTexture: attempt to use a deleted
  // object"). With a cache, aliasing is the NORMAL case (same frame → same
  // texture) and deletion happens in exactly one place (evictFrameTextures),
  // which skips anything currently bound. Map preserves insertion order;
  // lookups re-insert to mark recency (LRU). Bonus: revisited frames (loop
  // wrap, scrub back) re-bind instead of re-uploading, removing the
  // per-transition texImage2D stall for cache hits.
  private frameTexCache: Map<WeatherFrame, WebGLTexture> = new Map();
  // Cap on cached frame textures. Must exceed the max simultaneously-bound
  // set (tex0, tex1, texPrev, texNext = 4); the rest is reuse headroom. At
  // downsample 3 a radar frame texture is a few MB, so 8 ≈ ~20 MB GPU —
  // negligible next to the JS frame cache budget.
  private static readonly FRAME_TEX_CACHE_MAX = 8;

  /** Look up (or build) the texture for a decoded frame. The returned texture
   * is owned by frameTexCache — callers borrow it and must never delete it. */
  private getFrameTexture(frame: WeatherFrame): WebGLTexture {
    const hit = this.frameTexCache.get(frame);
    if (hit) {
      // Refresh recency: Map iterates in insertion order, so re-inserting
      // moves this entry to the newest end.
      this.frameTexCache.delete(frame);
      this.frameTexCache.set(frame, hit);
      return hit;
    }
    const tex = this.buildFrameTexture(frame);
    this.frameTexCache.set(frame, tex);
    this.evictFrameTextures();
    return tex;
  }

  /** Evict oldest cached frame textures over the cap. Never evicts a texture
   * that is currently bound to any slot — the one rule that makes
   * use-after-delete structurally impossible. */
  private evictFrameTextures() {
    if (!this.gl) return;
    const gl = this.gl;
    const bound = new Set<WebGLTexture | null>([
      this.tex0, this.tex1, this.texPrev, this.texNext,
    ]);
    // Guard: at most one pass over the cache per call.
    let guard = this.frameTexCache.size;
    for (const [frame, tex] of this.frameTexCache) {
      if (this.frameTexCache.size <= WeatherRasterLayer.FRAME_TEX_CACHE_MAX) break;
      if (guard-- <= 0) break;
      if (bound.has(tex)) continue; // in use — skip, try the next-oldest
      this.frameTexCache.delete(frame);
      gl.deleteTexture(tex);
    }
  }
  private paletteTex: WebGLTexture | null = null;
  // Flow texture — packed RGBA motion vectors from frame0 → frame1.
  // Only populated for motion-warped layers (SMOOTH==3). When the layer
  // uses a different smoothing mode, we still bind a 1×1 neutral texture
  // here to keep WebGL state consistent.
  private flowTex: WebGLTexture | null = null;
  // Motion-field cache: (keyA__keyB) → MotionField. Keeps us from
  // recomputing when scrubbing back-and-forth between adjacent frames.
  private motionCache = new Map<string, MotionField>();
  // Tracks the currently-bound flow field (for debug exposure to window)
  private currentFlow: MotionField | null = null;

  private frame0: WeatherFrame | null = null;
  private frame1: WeatherFrame | null = null;
  // Adjacent frames for multipass temporal smoothing (see texPrev/texNext).
  // null when not provided or when no temporal blur is configured.
  private framePrev: WeatherFrame | null = null;
  private frameNext: WeatherFrame | null = null;
  private _opacity: number;
  private _time = 0;
  // 1 when the playback loop is actively driving the scrubber, 0 when
  // paused. Forwarded to the shader as u_animating; SMOOTH==5 uses it to
  // suppress noise-floor pulse during animation.
  private _animating = 0;
  private currentBbox: number[] | null = null;
  // Frames may be pushed via setFrames() before onAdd() supplies a GL
  // context. We hold them here and upload during the first render.
  private pendingFrames: { frame0: WeatherFrame; frame1: WeatherFrame } | null = null;

  private uMvpLoc: WebGLUniformLocation | null = null;
  private uQuadBboxLoc: WebGLUniformLocation | null = null;
  private uTex0Loc: WebGLUniformLocation | null = null;
  private uTex1Loc: WebGLUniformLocation | null = null;
  private uPaletteLoc: WebGLUniformLocation | null = null;
  private uTimeLoc: WebGLUniformLocation | null = null;
  private uAnimatingLoc: WebGLUniformLocation | null = null;
  private uOpacityLoc: WebGLUniformLocation | null = null;
  private uTexSizeLoc: WebGLUniformLocation | null = null;
  private uKernelScaleLoc: WebGLUniformLocation | null = null;
  private uFlowLoc: WebGLUniformLocation | null = null;
  private uFlowMaxDispLoc: WebGLUniformLocation | null = null;

  // ── Three-pass FBO pipeline state (only populated when SMOOTH==4) ──
  // Programs and uniform locations for each of the three passes plus the two
  // ping-pong FBOs that carry intermediate results between them. All of these
  // stay null on layers that don't use multiPassSmooth.
  private pass1Program: WebGLProgram | null = null;
  private pass1UMvp: WebGLUniformLocation | null = null;
  private pass1UQuadBbox: WebGLUniformLocation | null = null;
  private pass1UTexPrev: WebGLUniformLocation | null = null;
  private pass1UTex0: WebGLUniformLocation | null = null;
  private pass1UTex1: WebGLUniformLocation | null = null;
  private pass1UTexNext: WebGLUniformLocation | null = null;
  private pass1UTime: WebGLUniformLocation | null = null;
  private pass1UTexSize: WebGLUniformLocation | null = null;
  private pass1UTemporalKernel: WebGLUniformLocation | null = null;

  private blurProgram: WebGLProgram | null = null;
  private blurUMvp: WebGLUniformLocation | null = null;
  private blurUQuadBbox: WebGLUniformLocation | null = null;
  private blurUInputI: WebGLUniformLocation | null = null;
  private blurUInputW: WebGLUniformLocation | null = null;
  private blurUPixelSize: WebGLUniformLocation | null = null;
  private blurUDir: WebGLUniformLocation | null = null;
  private blurUScale: WebGLUniformLocation | null = null;

  private colorizeProgram: WebGLProgram | null = null;
  private colorizeUMvp: WebGLUniformLocation | null = null;
  private colorizeUQuadBbox: WebGLUniformLocation | null = null;
  private colorizeUInputI: WebGLUniformLocation | null = null;
  private colorizeUInputW: WebGLUniformLocation | null = null;
  private colorizeUPalette: WebGLUniformLocation | null = null;
  private colorizeUPixelSize: WebGLUniformLocation | null = null;
  private colorizeUOpacity: WebGLUniformLocation | null = null;

  // Multi-pass FBO state. Two framebuffers, each with TWO color attachments
  // (MRT). The ping-pong flow is:
  //   Pass 1 (categorize)         → fboA  (writes to texA_inten and texA_weight)
  //   Pass 2 (H-blur)             → fboB  (reads from A, writes to B's two attachments)
  //   Pass 3 (V-blur)             → fboA  (reads from B, writes back to A)
  //   Pass 4 (colorize → map FBO) — reads from A
  // Splitting intensity from weight (rather than packing color into a single
  // FBO) is what gives multipass MRMS the smooth gradient appearance the
  // single-pass kernel produces; pass 4 does the palette lookup AFTER the
  // blur instead of before, so blurring smooths intensities (not finished
  // colors), avoiding chunky cell edges at intensity gradients.
  private fboA: WebGLFramebuffer | null = null;
  private texA_inten: WebGLTexture | null = null;
  private texA_weight: WebGLTexture | null = null;
  private fboB: WebGLFramebuffer | null = null;
  private texB_inten: WebGLTexture | null = null;
  private texB_weight: WebGLTexture | null = null;
  private fboWidth = 0;
  private fboHeight = 0;

  private disposed = false;

  constructor(opts: WeatherRasterLayerOptions) {
    this.id = opts.id;
    this.config = opts.config;
    this._opacity = opts.opacity ?? 0.7;
  }

  // ─── CustomLayerInterface lifecycle ─────────────────────────────────────

  /** Called by MapLibre when this layer is added to the map. We compile
   *  shaders, build the palette LUT, and allocate the subdivided grid VAO
   *  here — not in the constructor, because the GL context belongs to
   *  MapLibre and isn't available until now. */
  onAdd(map: MaplibreMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
    this.map = map;
    this.gl = gl;

    // ── Build shader program ──
    const decodeMode = this.config.decode === "R" ? 1
                     : this.config.decode === "RG" ? 2
                     : 3;
    // BAND_CHANNEL: 0=R (default), 1=G, 2=B. Only used by DECODE_MODE==1.
    const bandChannel = this.config.band === "G" ? 1
                      : this.config.band === "B" ? 2
                      : 0;
    // DISCARD_ZERO: 1=discard byte-0 pixels (default — most layers use byte 0
    // as a no-data sentinel). Set to 0 in LayerConfig (`discardZero: false`)
    // for layers where byte 0 is legitimate data.
    const discardZero = this.config.discardZero === false ? 0 : 1;
    // SMOOTH: 0 = no blur; 1 = basic color-space blur (continuous layers);
    // 2 = full pipeline (bilinear-in-color-space + Gaussian + threshold) for
    // categorical precip-type layers like MRMS;
    // 3 = motion-warped pipeline (uses optical flow field between frames —
    // requires config.motionWarp = true, only sensible for precip-type data);
    // 4 = three-pass separable-Gaussian via FBOs (categorize → H-blur →
    // V-blur+colorize). Used when config.multiPassSmooth is set; produces
    // smoother results at high zoom by sampling the prior pass with hardware
    // bilinear filtering rather than stacking 29 NEAREST taps that visibly
    // emboss the source-pixel grid into the output.
    // Priority order:
    //   smooth=4 (multipass) takes precedence over smooth=3 (single-pass
    //     motion-warp) because pass 1 of the multipass pipeline now performs
    //     the same flow-based UV displacement that SMOOTH==3 does, but feeds
    //     the warped result into the separable Gaussian instead of stopping
    //     at a single texture sample. So when both multiPassSmooth and
    //     motionWarp are set, you get warped + smoothly blurred output.
    //   smooth=3 stays as the path for legacy configs that have motionWarp
    //     but not multiPassSmooth (or where multipass FBOs failed to alloc).
    //   smooth=5 is the simple-bilinear A/B path: bypass the category-aware
    //     kernel machinery entirely and just do sentinel-safe bilinear +
    //     temporal blend + LUT. Selected when config.simplePrecipType is set
    //     alongside precipTypePalettes. Overrides multiPassSmooth and
    //     motionWarp since the whole point is to be simple.
    const smooth = !this.config.smooth ? 0
                 : this.config.simplePrecipType && this.config.precipTypePalettes ? 5
                 : this.config.multiPassSmooth && this.config.precipTypePalettes ? 4
                 : this.config.motionWarp ? 3
                 : this.config.precipTypePalettes ? 2
                 : 1;

    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER, "vertex");
    const rgbPrecip = !!this.config.simplePrecipType && !!this.config.precipTypePalettes ? 1 : 0;
    // QUANT_254: 1 = updated GFS pipeline quantization (byte = 1 + 254*t,
    // byte 0 = NoData) → dequantize with (byte-1)/254. 0 = legacy byte/255.
    const quant254 = this.config.quantized254 ? 1 : 0;

    const fs = compileShader(gl, gl.FRAGMENT_SHADER,
      FRAGMENT_SHADER({ DECODE_MODE: decodeMode, SMOOTH: smooth, BAND_CHANNEL: bandChannel, DISCARD_ZERO: discardZero, RGB_PRECIP: rgbPrecip, QUANT_254: quant254 }),
      "fragment"
    );
    this.program = linkProgram(gl, vs, fs);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    this.uMvpLoc      = gl.getUniformLocation(this.program, "u_mvp");
    this.uQuadBboxLoc = gl.getUniformLocation(this.program, "u_quadBbox");
    this.uTex0Loc     = gl.getUniformLocation(this.program, "u_tex0");
    this.uTex1Loc     = gl.getUniformLocation(this.program, "u_tex1");
    this.uPaletteLoc  = gl.getUniformLocation(this.program, "u_palette");
    this.uTimeLoc     = gl.getUniformLocation(this.program, "u_time");
    this.uAnimatingLoc = gl.getUniformLocation(this.program, "u_animating");
    this.uOpacityLoc  = gl.getUniformLocation(this.program, "u_opacity");
    this.uTexSizeLoc  = gl.getUniformLocation(this.program, "u_texSize");
    this.uKernelScaleLoc = gl.getUniformLocation(this.program, "u_kernelScale");
    this.uFlowLoc        = gl.getUniformLocation(this.program, "u_flow");
    this.uFlowMaxDispLoc = gl.getUniformLocation(this.program, "u_flowMaxDisp");

    // ── Build multipass pipeline when SMOOTH==4 ──
    // The main program above is still built (unused for SMOOTH==4 layers but
    // harmless to keep) — keeping the shared lifecycle simple. The three pass
    // programs share the same vertex shader and the existing grid VAO. They
    // only differ in their fragment shaders.
    if (smooth === 4) {
      const sharedVs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER, "multipass-vertex");

      // Pass 1 — categorize source TIFs, write per-category (intensity, weight)
      // pairs into two MRT FBO attachments.
      const fs1 = compileShader(gl, gl.FRAGMENT_SHADER, PASS1_FRAGMENT_SHADER, "pass1");
      this.pass1Program = linkProgram(gl, sharedVs, fs1);
      gl.deleteShader(fs1);
      this.pass1UMvp        = gl.getUniformLocation(this.pass1Program, "u_mvp");
      this.pass1UQuadBbox   = gl.getUniformLocation(this.pass1Program, "u_quadBbox");
      this.pass1UTexPrev    = gl.getUniformLocation(this.pass1Program, "u_texPrev");
      this.pass1UTex0       = gl.getUniformLocation(this.pass1Program, "u_tex0");
      this.pass1UTex1       = gl.getUniformLocation(this.pass1Program, "u_tex1");
      this.pass1UTexNext    = gl.getUniformLocation(this.pass1Program, "u_texNext");
      this.pass1UTime       = gl.getUniformLocation(this.pass1Program, "u_time");
      this.pass1UTexSize    = gl.getUniformLocation(this.pass1Program, "u_texSize");
      this.pass1UTemporalKernel = gl.getUniformLocation(this.pass1Program, "u_temporalKernel");

      // Blur — same shader runs for H and V, distinguished by u_blurDir.
      // Reads two FBOs (intensity + weight) and writes two via MRT.
      const fsBlur = compileShader(gl, gl.FRAGMENT_SHADER, BLUR_FRAGMENT_SHADER, "blur");
      this.blurProgram = linkProgram(gl, sharedVs, fsBlur);
      gl.deleteShader(fsBlur);
      this.blurUMvp       = gl.getUniformLocation(this.blurProgram, "u_mvp");
      this.blurUQuadBbox  = gl.getUniformLocation(this.blurProgram, "u_quadBbox");
      this.blurUInputI    = gl.getUniformLocation(this.blurProgram, "u_inputI");
      this.blurUInputW    = gl.getUniformLocation(this.blurProgram, "u_inputW");
      this.blurUPixelSize = gl.getUniformLocation(this.blurProgram, "u_pixelSize");
      this.blurUDir       = gl.getUniformLocation(this.blurProgram, "u_blurDir");
      this.blurUScale     = gl.getUniformLocation(this.blurProgram, "u_blurScale");

      // Colorize — read smoothed intensities and weights, look up palette,
      // blend, write final RGBA to map's framebuffer.
      const fsColor = compileShader(gl, gl.FRAGMENT_SHADER, COLORIZE_FRAGMENT_SHADER, "colorize");
      this.colorizeProgram = linkProgram(gl, sharedVs, fsColor);
      gl.deleteShader(fsColor);
      this.colorizeUMvp      = gl.getUniformLocation(this.colorizeProgram, "u_mvp");
      this.colorizeUQuadBbox = gl.getUniformLocation(this.colorizeProgram, "u_quadBbox");
      this.colorizeUInputI   = gl.getUniformLocation(this.colorizeProgram, "u_inputI");
      this.colorizeUInputW   = gl.getUniformLocation(this.colorizeProgram, "u_inputW");
      this.colorizeUPalette  = gl.getUniformLocation(this.colorizeProgram, "u_palette");
      this.colorizeUPixelSize= gl.getUniformLocation(this.colorizeProgram, "u_pixelSize");
      this.colorizeUOpacity  = gl.getUniformLocation(this.colorizeProgram, "u_opacity");

      gl.deleteShader(sharedVs);
    }

    // ── Build subdivided grid VAO ──
    // We render an (N+1)×(N+1) lattice of vertices in unit-quad space [0,1]²
    // wired up as 2·N² triangles via an index buffer. The vertex shader maps
    // each unit-quad position into unit-mercator coords for the current
    // frame's bbox, then the map-supplied projection matrix turns those into
    // clip-space — including any tilt/perspective. With each cell small in
    // screen space, perspective foreshortening is interpolated finely enough
    // that the raster appears glued to the basemap rather than floating.
    const grid = buildGridGeometry(SUBDIVISION_N);
    this.indexCount = grid.indices.length;

    // VAOs are a WebGL2 feature. MapLibre 5 with default contextType
    // 'webgl2withfallback' should give us WebGL2 on any browser made in
    // the last decade — but if we somehow ended up on WebGL1, fail loudly.
    if (!isWebGL2(gl)) {
      throw new Error("WeatherRasterLayer requires WebGL2; map context is WebGL1.");
    }
    const gl2 = gl;
    const vaoObj = gl2.createVertexArray();
    if (!vaoObj) throw new Error("createVertexArray returned null");
    this.vao = vaoObj;
    gl2.bindVertexArray(this.vao);

    const vbuf = gl.createBuffer();
    if (!vbuf) throw new Error("createBuffer returned null");
    this.vertexBuffer = vbuf;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, grid.vertices, gl.STATIC_DRAW);

    const ibuf = gl.createBuffer();
    if (!ibuf) throw new Error("createBuffer returned null");
    this.indexBuffer = ibuf;
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, grid.indices, gl.STATIC_DRAW);

    const aPositionLoc = gl.getAttribLocation(this.program, "a_position");
    gl.enableVertexAttribArray(aPositionLoc);
    gl.vertexAttribPointer(aPositionLoc, 2, gl.FLOAT, false, 0, 0);
    gl2.bindVertexArray(null);

    // Palette + neutral flow texture
    this.paletteTex = this.buildPaletteTexture();
    this.flowTex = this.buildNeutralFlowTexture();

    // Process any frames that arrived via setFrames() before onAdd
    if (this.pendingFrames) {
      const pf = this.pendingFrames;
      this.pendingFrames = null;
      this.setFrames(pf.frame0, pf.frame1);
    }

    map.triggerRepaint();
  }

  /** Called by MapLibre when the layer is removed via map.removeLayer().
   *  Releases all GL resources owned by this layer. */
  onRemove(_map: MaplibreMap, gl: WebGL2RenderingContext | WebGLRenderingContext) {
    // Frame textures are owned by the cache — delete each exactly once there.
    // (Slots are borrowed references; deleting per-slot would double-delete
    // shared textures and miss cached-but-unbound ones.)
    for (const tex of this.frameTexCache.values()) gl.deleteTexture(tex);
    this.frameTexCache.clear();
    if (this.flowTex) gl.deleteTexture(this.flowTex);
    if (this.paletteTex) gl.deleteTexture(this.paletteTex);
    if (this.vertexBuffer) gl.deleteBuffer(this.vertexBuffer);
    if (this.indexBuffer) gl.deleteBuffer(this.indexBuffer);
    if (this.program) gl.deleteProgram(this.program);
    if (this.vao && isWebGL2(gl)) gl.deleteVertexArray(this.vao);

    // Multipass cleanup — these are null on layers that don't use multiPassSmooth.
    if (this.texA_inten)  gl.deleteTexture(this.texA_inten);
    if (this.texA_weight) gl.deleteTexture(this.texA_weight);
    if (this.texB_inten)  gl.deleteTexture(this.texB_inten);
    if (this.texB_weight) gl.deleteTexture(this.texB_weight);
    if (this.fboA)        gl.deleteFramebuffer(this.fboA);
    if (this.fboB)        gl.deleteFramebuffer(this.fboB);
    if (this.pass1Program)    gl.deleteProgram(this.pass1Program);
    if (this.blurProgram)     gl.deleteProgram(this.blurProgram);
    if (this.colorizeProgram) gl.deleteProgram(this.colorizeProgram);
    this.texA_inten = this.texA_weight = this.texB_inten = this.texB_weight = null;
    this.fboA = this.fboB = null;
    this.pass1Program = this.blurProgram = this.colorizeProgram = null;

    this.tex0 = this.tex1 = this.texPrev = this.texNext = null;
    this.flowTex = this.paletteTex = null;
    this.vertexBuffer = this.indexBuffer = null;
    this.program = null;
    this.vao = null;
    this.motionCache.clear();
    this.gl = null;
    this.map = null;

    if (typeof window !== "undefined") {
      delete (window as any).__stormDebug;
    }
  }

  /** Called by MapLibre on every render frame. We get the projection matrix
   *  for free as args.defaultProjectionData.mainMatrix — it converts unit-
   *  mercator coords directly to clip-space, including perspective. */
  render(
    gl: WebGL2RenderingContext | WebGLRenderingContext,
    args: { defaultProjectionData: { mainMatrix: number[] | Float32Array } } | Float32Array | number[],
  ) {
    if (this.disposed) return;
    if (!this.program || !this.vao || !this.frame0 || !this.tex0 || !this.currentBbox) return;
    if (!isWebGL2(gl)) return;
    const gl2 = gl;

    // MapLibre's render arg shape changed across versions. Older code passed
    // a bare matrix; newer code (5.x globe-aware) passes an args object with
    // defaultProjectionData.mainMatrix. Accept both.
    const matrix = (args as any)?.defaultProjectionData?.mainMatrix
                   ?? (args as any)?.matrix
                   ?? (args as any);

    // Convert frame bbox (lng/lat) to unit-mercator. Cached only when bbox
    // changes, but the cost is so small it's not worth the bookkeeping.
    const nw = MercatorCoordinate.fromLngLat({ lng: this.currentBbox[0], lat: this.currentBbox[3] });
    const se = MercatorCoordinate.fromLngLat({ lng: this.currentBbox[2], lat: this.currentBbox[1] });
    const mercBbox = [nw.x, nw.y, se.x, se.y];

    // ── Three-pass multipass branch ──
    // Active when LayerConfig.multiPassSmooth is set. Renders into two
    // ping-pong FBOs and a final pass to MapLibre's framebuffer. Falls
    // through to the single-pass path on any allocation failure so a layer
    // with multiPassSmooth set never breaks if the GPU runs out of memory
    // or hits an FBO incompatibility.
    if (this.pass1Program && this.blurProgram && this.colorizeProgram) {
      const W = gl.drawingBufferWidth | 0;
      const H = gl.drawingBufferHeight | 0;
      if (W > 0 && H > 0 && this.ensureFBOs(W, H)) {
        this.renderMultipass(gl2, matrix as Float32Array, mercBbox);
        gl2.bindVertexArray(null);
        return;
      }
      // FBO allocation failed — fall through to single-pass below.
    }

    gl.useProgram(this.program);
    gl2.bindVertexArray(this.vao);

    gl.uniformMatrix4fv(this.uMvpLoc, false, matrix as Float32Array);
    gl.uniform4f(this.uQuadBboxLoc, mercBbox[0], mercBbox[1], mercBbox[2], mercBbox[3]);
    gl.uniform1f(this.uTimeLoc, this._time);
    gl.uniform1f(this.uAnimatingLoc, this._animating);
    gl.uniform1f(this.uOpacityLoc, this._opacity);
    gl.uniform2f(this.uTexSizeLoc, this.frame0.width, this.frame0.height);

    // Compute kernel scale from the map's current zoom. The kernel offsets
    // were tuned at zoom ~6 for MRMS at downsample 2 (one source texel ≈
    // one screen pixel). Above that zoom each source texel covers many
    // screen pixels, so we grow the kernel so it stays roughly constant in
    // screen size — otherwise the visible "blocky" texel grid is what the
    // user actually sees instead of the smoothed data. Below the reference
    // zoom we leave the kernel alone (clamped to 1.0) because a smaller
    // kernel just becomes a no-op rather than producing useful sharpness.
    //
    // Cap at 4.0 because beyond that the kernel starts pulling from samples
    // far enough away to smear genuine small-scale features (e.g., narrow
    // squall lines, hook echoes) into the surrounding precip.
    const REF_ZOOM = 6.0;
    const MAX_SCALE = 4.0;
    const zoom = this.map?.getZoom() ?? REF_ZOOM;
    const kernelScale = Math.max(1.0, Math.min(MAX_SCALE, Math.pow(2.0, zoom - REF_ZOOM)));
    gl.uniform1f(this.uKernelScaleLoc, kernelScale);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex0);
    gl.uniform1i(this.uTex0Loc, 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.tex1 || this.tex0);
    gl.uniform1i(this.uTex1Loc, 1);

    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    gl.uniform1i(this.uPaletteLoc, 2);

    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.flowTex!);
    gl.uniform1i(this.uFlowLoc, 3);
    gl.uniform1f(this.uFlowMaxDispLoc, 128.0);

    // MapLibre docs: "the layer can assume blending and depth state is set
    // to allow the layer to properly blend and clip other layers." We still
    // set our blend func explicitly because we need ONE / ONE_MINUS_SRC_ALPHA
    // (premultiplied), which differs from MapLibre's default of SRC_ALPHA /
    // ONE_MINUS_SRC_ALPHA. Depth is disabled by MapLibre for 2D layers.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

    gl2.bindVertexArray(null);
  }

  /** Three-pass rendering for multiPassSmooth layers. Called from render()
   * after all the common setup. The single bindVertexArray(null) cleanup
   * lives in render() to keep the call sites symmetric. */
  private renderMultipass(
    gl: WebGL2RenderingContext,
    matrix: Float32Array,
    mercBbox: number[],
  ) {
    if (!this.frame0 || !this.tex0 || !this.paletteTex) return;
    if (!this.fboA || !this.fboB) return;
    if (!this.texA_inten || !this.texA_weight) return;
    if (!this.texB_inten || !this.texB_weight) return;
    if (!this.pass1Program || !this.blurProgram || !this.colorizeProgram) return;

    // Save the current framebuffer binding so we can restore it for the
    // final pass. MapLibre may have bound its own framebuffer.
    const mapFbo = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;
    const W = this.fboWidth, H = this.fboHeight;

    gl.bindVertexArray(this.vao);

    // ── Pass 1: source TIFs → fboA (intensity + weight via MRT) ──
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboA);
    // Activate both color attachments. MRT requires drawBuffers to mark
    // attachments 0 and 1 as receiving the layout(location=0/1) outputs.
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    gl.viewport(0, 0, W, H);
    // Pass 1 writes only inside the bbox-projected geometry; outside stays
    // at the cleared zero so blur passes don't read garbage. Disable blend
    // — pass 1 is a direct overwrite, not a composite.
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.pass1Program);
    gl.uniformMatrix4fv(this.pass1UMvp, false, matrix);
    gl.uniform4f(this.pass1UQuadBbox, mercBbox[0], mercBbox[1], mercBbox[2], mercBbox[3]);
    gl.uniform1f(this.pass1UTime, this._time);
    gl.uniform2f(this.pass1UTexSize, this.frame0.width, this.frame0.height);
    gl.uniform1f(this.pass1UTemporalKernel, this.config.temporalBlurWidth ?? 1.0);

    // Bind the four temporal frame textures. Missing neighbors fall back to
    // central frames — graceful degradation at timeline boundaries.
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texPrev || this.tex0);
    gl.uniform1i(this.pass1UTexPrev, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.tex0);
    gl.uniform1i(this.pass1UTex0, 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.tex1 || this.tex0);
    gl.uniform1i(this.pass1UTex1, 2);
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.texNext || this.tex1 || this.tex0);
    gl.uniform1i(this.pass1UTexNext, 3);

    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

    // ── Blur scale — same logic as before, just used for both H and V ──
    const REF_ZOOM = 6.0;
    const MIN_SCALE = 0.5;
    const MAX_SCALE = 2.0;
    const zoom = this.map?.getZoom() ?? REF_ZOOM;
    const blurScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, MIN_SCALE * Math.pow(2.0, zoom - REF_ZOOM)));

    // ── Pass 2: H-blur, fboA → fboB (both attachments via MRT) ──
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboB);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.blurProgram);
    gl.uniformMatrix4fv(this.blurUMvp, false, matrix);
    gl.uniform4f(this.blurUQuadBbox, mercBbox[0], mercBbox[1], mercBbox[2], mercBbox[3]);
    gl.uniform2f(this.blurUPixelSize, 1.0 / W, 1.0 / H);
    gl.uniform2f(this.blurUDir, 1.0, 0.0);
    gl.uniform1f(this.blurUScale, blurScale);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texA_inten);
    gl.uniform1i(this.blurUInputI, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texA_weight);
    gl.uniform1i(this.blurUInputW, 1);

    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

    // ── Pass 3: V-blur, fboB → fboA (ping-pong) ──
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboA);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.uniform2f(this.blurUDir, 0.0, 1.0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texB_inten);
    gl.uniform1i(this.blurUInputI, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texB_weight);
    gl.uniform1i(this.blurUInputW, 1);

    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);

    // ── Pass 4: colorize, fboA → map's framebuffer ──
    gl.bindFramebuffer(gl.FRAMEBUFFER, mapFbo);
    gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    gl.useProgram(this.colorizeProgram);
    gl.uniformMatrix4fv(this.colorizeUMvp, false, matrix);
    gl.uniform4f(this.colorizeUQuadBbox, mercBbox[0], mercBbox[1], mercBbox[2], mercBbox[3]);
    gl.uniform2f(this.colorizeUPixelSize, 1.0 / W, 1.0 / H);
    gl.uniform1f(this.colorizeUOpacity, this._opacity);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texA_inten);
    gl.uniform1i(this.colorizeUInputI, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.texA_weight);
    gl.uniform1i(this.colorizeUInputW, 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.paletteTex);
    gl.uniform1i(this.colorizeUPalette, 2);

    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_SHORT, 0);
  }

  // ─── Texture construction ────────────────────────────────────────────────

  private buildPaletteTexture(): WebGLTexture {
    const gl = this.gl;
    if (!gl) throw new Error("buildPaletteTexture called before onAdd");
    const tex = gl.createTexture();
    if (!tex) throw new Error("createTexture returned null");
    gl.bindTexture(gl.TEXTURE_2D, tex);

    // Precip-type encoded layers (MRMS): build a flat 256-entry LUT with
    // transition zones at the category boundaries. Otherwise use the normal
    // continuous palette.
    const palette = this.config.precipTypePalettes
      ? buildPrecipTypeLUT(this.config.precipTypePalettes)
      : buildPaletteTexture(this.config.palette);

    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 256, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, palette);

    // LINEAR for continuous palettes (temperature, wind). NEAREST for
    // precip-type LUT so the abrupt category boundaries aren't smoothed out
    // by palette sampling (the shader already blurs the DATA, not the lookup).
    const paletteFilter = this.config.precipTypePalettes ? gl.NEAREST : gl.LINEAR;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, paletteFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, paletteFilter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  private buildFrameTexture(frame: WeatherFrame): WebGLTexture {
    const gl = this.gl;
    if (!gl) throw new Error("buildFrameTexture called before onAdd");

    if (this.config.simplePrecipType && this.config.precipTypePalettes) {
      const src = frame.rawData;
      const pixels = frame.width * frame.height;
      const dst = new Uint8Array(pixels * 4);

      // ── New 3-band RGB radar decode (per backend spec) ──────────────────
      // The COG carries three bands, packed by the worker into RGBA:
      //   R (channel 0) = reflectivity, 0..100 dBZ → 0..255. 0 = no precip;
      //                   255 = 100 dBZ (NOT no-data). Used as intensity.
      //   G (channel 1) = precip type, continuous 0..1 → 0..255, per the
      //                   backend spec:
      //                     byte <  51  (phase < 0.2) → snow
      //                     51..127     (0.2..0.5)    → sleet
      //                     byte >= 128 (phase > 0.5) → rain
      //   B (channel 2) = radar coverage, categorical: 0 = outside coverage
      //                   (transparent), 1 = within coverage (show).
      //
      // Output contract (unchanged — the RGB_PRECIP shader consumes this):
      //   dst R = rain intensity, G = sleet intensity, B = snow intensity,
      //   A = 255 where shown / 0 where transparent. The shader remaps each
      //   channel back into its palette byte range and composites by the
      //   intensity-weighted share, so exactly one of R/G/B is non-zero per
      //   pixel (a single hard category) and the others stay 0.
      //
      // Phase thresholds in byte space (0..1 → 0..255):
      //   0.2 → 51 (snow/sleet split), 0.5 → 128 (sleet/rain split).
      const SNOW_MAX_BYTE  = 51;   // type byte <  51  → snow
      const SLEET_MAX_BYTE = 128;  // 51..127 → sleet; >= 128 → rain

      // This path only runs for multi-channel (RGBA) frames carrying the
      // 3-band data. A single-channel frame can't supply type+coverage, so
      // fall back to drawing nothing for it (shouldn't happen in practice
      // now that radar COGs are 3-band).
      if (frame.channels === 1) {
        return makeTexture(gl, dst, frame.width, frame.height, true, 4);
      }

      for (let i = 0; i < pixels; i++) {
        const sp = i << 2;
        const reflect  = src[sp];     // R: intensity (dBZ byte)
        const typeByte = src[sp + 1]; // G: precip type
        const coverage = src[sp + 2]; // B: coverage mask (0 / 1)
        const dp = sp;

        // Transparent where outside radar coverage, or no precipitation.
        if (coverage === 0 || reflect === 0) {
          dst[dp] = dst[dp + 1] = dst[dp + 2] = dst[dp + 3] = 0;
          continue;
        }

        // Route the intensity into the channel for this pixel's category
        // (original spec: low byte = snow, high byte = rain).
        dst[dp] = dst[dp + 1] = dst[dp + 2] = 0;
        if (typeByte < SNOW_MAX_BYTE) {
          dst[dp + 2] = reflect;       // snow → B channel
        } else if (typeByte < SLEET_MAX_BYTE) {
          dst[dp + 1] = reflect;       // sleet → G channel
        } else {
          dst[dp] = reflect;           // rain → R channel
        }
        dst[dp + 3] = 255;
      }

      return makeTexture(gl, dst, frame.width, frame.height, true, 4);
    }

    const useLinear = !this.config.precipTypePalettes;
    return makeTexture(gl, frame.rawData, frame.width, frame.height, useLinear, frame.channels);
  }

  /** Build a 1×1 texture encoding "no motion, no confidence" — used when a
   * real flow field isn't available (other smooth modes or uncomputed pairs). */
  private buildNeutralFlowTexture(): WebGLTexture {
    const gl = this.gl;
    if (!gl) throw new Error("buildNeutralFlowTexture called before onAdd");
    // 127 = zero displacement (center of [0..255] encoding), 0 = no confidence
    const data = new Uint8Array([127, 127, 0, 255]);
    const tex = gl.createTexture();
    if (!tex) throw new Error("createTexture returned null");
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /** Allocate or reallocate the two MRT framebuffers used by the multipass
   * pipeline. Each FBO has TWO color attachments — one for the per-category
   * weighted-intensity values, one for the per-category weights. Splitting
   * the encoding into intensity and weight separately is what enables the
   * blur passes to smooth intensities (per-category position within the
   * palette) before color is resolved, giving smooth gradients matching the
   * single-pass kernel's appearance.
   *
   * Called from render() when the canvas size changes or on the first
   * multipass render after onAdd. Returns false (so the caller falls back
   * to the single-pass path) if any allocation or FBO completeness check
   * fails — typically a GPU memory pressure issue.
   *
   * The FBO textures use LINEAR filtering. Hardware bilinear sampling on
   * intermediate FBO contents is safe because all four channels are
   * continuous quantities (sums of weights/intensities) — there are no
   * discrete category boundaries to skip across.
   *
   * RGBA8 precision: pass 1 outputs are bounded in [0,1] (weights sum to
   * 1, intensities are in [0,1], weight×intensity ≤ 1) and Gaussian-blurred
   * results stay in [0,1] because the kernel weights sum to 1. RGBA16F
   * would give extra precision headroom but RGBA8 is universally supported
   * and the visible-difference threshold for radar is well above 8-bit. */
  private ensureFBOs(width: number, height: number): boolean {
    const gl = this.gl;
    if (!gl || !isWebGL2(gl)) return false;
    if (
      this.fboA && this.fboB &&
      this.texA_inten && this.texA_weight &&
      this.texB_inten && this.texB_weight &&
      this.fboWidth === width && this.fboHeight === height
    ) {
      return true;  // already sized correctly
    }

    // Free previous allocations on resize.
    const cleanup = (
      tex0: WebGLTexture | null,
      tex1: WebGLTexture | null,
      fbo: WebGLFramebuffer | null,
    ) => {
      if (tex0) gl.deleteTexture(tex0);
      if (tex1) gl.deleteTexture(tex1);
      if (fbo)  gl.deleteFramebuffer(fbo);
    };
    cleanup(this.texA_inten, this.texA_weight, this.fboA);
    cleanup(this.texB_inten, this.texB_weight, this.fboB);
    this.fboA = this.fboB = null;
    this.texA_inten = this.texA_weight = null;
    this.texB_inten = this.texB_weight = null;

    const allocTexture = (): WebGLTexture | null => {
      const tex = gl.createTexture();
      if (!tex) return null;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return tex;
    };

    // Allocate four textures (two per FBO).
    const tA0 = allocTexture(); const tA1 = allocTexture();
    const tB0 = allocTexture(); const tB1 = allocTexture();
    if (!tA0 || !tA1 || !tB0 || !tB1) {
      [tA0, tA1, tB0, tB1].forEach(t => t && gl.deleteTexture(t));
      return false;
    }

    // Allocate framebuffers and bind both color attachments to each.
    const buildFbo = (intenTex: WebGLTexture, weightTex: WebGLTexture): WebGLFramebuffer | null => {
      const fbo = gl.createFramebuffer();
      if (!fbo) return null;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, intenTex,  0);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, weightTex, 0);
      const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
      if (status !== gl.FRAMEBUFFER_COMPLETE) {
        console.warn(`[WeatherRasterLayer] FBO incomplete: 0x${status.toString(16)}`);
        gl.deleteFramebuffer(fbo);
        return null;
      }
      return fbo;
    };

    const fA = buildFbo(tA0, tA1);
    const fB = buildFbo(tB0, tB1);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (!fA || !fB) {
      gl.deleteTexture(tA0); gl.deleteTexture(tA1);
      gl.deleteTexture(tB0); gl.deleteTexture(tB1);
      if (fA) gl.deleteFramebuffer(fA);
      if (fB) gl.deleteFramebuffer(fB);
      return false;
    }

    this.fboA = fA; this.texA_inten = tA0; this.texA_weight = tA1;
    this.fboB = fB; this.texB_inten = tB0; this.texB_weight = tB1;
    this.fboWidth = width;
    this.fboHeight = height;
    return true;
  }

  /** Upload a new motion field to the flow texture. The layer will start using
   * it on the next render. */
  private setFlow(field: MotionField): void {
    const gl = this.gl;
    if (!gl || !this.flowTex) return;
    gl.bindTexture(gl.TEXTURE_2D, this.flowTex);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA,
      field.width, field.height, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, field.rgba,
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    this.currentFlow = field;
  }

  /** Cache key for a motion field between two frames, using their bbox
   * signatures (which are stable across decode runs for the same source file). */
  private motionKey(fa: WeatherFrame, fb: WeatherFrame): string {
    // Use frame identity (object reference) via WeakMap would be cleaner, but
    // Map keys based on bbox + size should be unique enough for our purposes.
    return `${fa.bbox.join(",")}__${fa.width}x${fa.height}::${fb.bbox.join(",")}`;
  }

  /** Compute and cache the motion field for a frame pair, if not already
   * cached. Expensive (100-200ms); called at most once per frame pair. */
  private ensureMotionFieldFor(fa: WeatherFrame, fb: WeatherFrame): MotionField | null {
    if (!this.config.motionWarp) return null;
    // Same-frame case: zero motion
    if (fa === fb) return null;
    const key = this.motionKey(fa, fb);
    const cached = this.motionCache.get(key);
    if (cached) return cached;

    if (fa.width !== fb.width || fa.height !== fb.height) {
      console.warn("[WeatherRasterLayer] frame pair has mismatched dimensions — skipping motion estimation");
      return null;
    }

    const field = estimateMotion(fa.rawData, fb.rawData, fa.width, fa.height);
    this.motionCache.set(key, field);
    // Cap cache size to prevent unbounded growth
    if (this.motionCache.size > 40) {
      const firstKey = this.motionCache.keys().next().value;
      if (firstKey) this.motionCache.delete(firstKey);
    }
    return field;
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Set the two frames to interpolate between. time=0 shows frame0, time=1
   * shows frame1. Pass the same frame twice to display a single frame.
   *
   * Reuses the old `tex1` as the new `tex0` when frames shift forward by one
   * keyframe — eliminating the GPU upload stall at keyframe boundaries.
   *
   * Safe to call before the layer is added to the map: the most recent
   * pending pair is buffered and applied during onAdd().
   */
  setFrames(frame0: WeatherFrame, frame1: WeatherFrame) {
    const sameA = this.frame0 === frame0;
    const sameB = this.frame1 === frame1;
    if (sameA && sameB) return;

    // Buffer until the GL context is available
    if (!this.gl) {
      this.pendingFrames = { frame0, frame1 };
      this.frame0 = frame0;
      this.frame1 = frame1;
      this.currentBbox = frame0.bbox;
      return;
    }
    const gl = this.gl;
    void gl; // texture builds/deletes now live in getFrameTexture/evictFrameTextures

    // Textures come from the frame→texture cache: same frame in both slots
    // (single-frame display at a hole edge) simply borrows the same texture
    // twice, which is legal to bind and safe — no slot ever owns or deletes
    // a texture, so the old shift/alias/delete hazards are gone. A one-step
    // advance (A,B)→(B,C) is a cache hit for B (no upload) and one build for
    // C — the same upload cost as the old shift optimization.
    this.tex0 = this.getFrameTexture(frame0);
    this.tex1 = frame1 === frame0 ? this.tex0 : this.getFrameTexture(frame1);

    this.frame0 = frame0;
    this.frame1 = frame1;
    this.currentBbox = frame0.bbox;

    // Compute motion field for the new pair (only for motion-warped layers).
    // Skipped when frame0 === frame1 (single-frame display, no motion to infer).
    if (this.config.motionWarp && frame0 !== frame1) {
      const field = this.ensureMotionFieldFor(frame0, frame1);
      if (field) {
        this.setFlow(field);
      }
      this.exposeDebug();
    }

    this.map?.triggerRepaint();
  }

  /** Provide the four-frame temporal window for layers that opt into
   * multi-frame temporal smoothing (config.temporalBlurWidth > 1).
   *
   * `framePrev` and `frameNext` are optional — pass null at the timeline
   * boundaries or when the adjacent frame isn't yet decoded. The renderer
   * gracefully degrades to standard cross-fade in those cases (the missing
   * neighbor is treated as a duplicate of the central frame, which
   * contributes no motion-blur signal).
   *
   * This calls setFrames() internally for the central pair, so the existing
   * tex0/tex1 reuse optimizations still apply. The prev/next textures are
   * managed independently with their own diff-and-rebuild logic.
   */
  setTemporalFrames(
    framePrev: WeatherFrame | null,
    frame0: WeatherFrame,
    frame1: WeatherFrame,
    frameNext: WeatherFrame | null,
  ) {
    // Update the central pair via the existing path — this preserves the
    // tex1→tex0 shift optimization that keeps GPU upload stalls minimal
    // during normal playback.
    this.setFrames(frame0, frame1);

    if (!this.gl) {
      // No GL context yet; the prev/next textures will be created the first
      // time setTemporalFrames is called after onAdd. setFrames already
      // buffered frame0/frame1; we keep the prev/next references so we can
      // build their textures lazily.
      this.framePrev = framePrev;
      this.frameNext = frameNext;
      return;
    }
    const gl = this.gl;
    void gl;

    // Prev/next are borrowed cache references like tex0/tex1 — no per-slot
    // deletes. If framePrev happens to be the same WeatherFrame as one of the
    // central pair, they share a texture; the cache makes that safe.
    if (this.framePrev !== framePrev) {
      this.texPrev = framePrev ? this.getFrameTexture(framePrev) : null;
      this.framePrev = framePrev;
    }
    if (this.frameNext !== frameNext) {
      this.texNext = frameNext ? this.getFrameTexture(frameNext) : null;
      this.frameNext = frameNext;
    }
    this.map?.triggerRepaint();
  }

  /** Expose the current frame pair and motion field on a window global so
   * the debug visualizer can be invoked from the browser console. */
  private exposeDebug() {
    if (typeof window === "undefined") return;
    (window as any).__stormDebug = {
      frame0: this.frame0,
      frame1: this.frame1,
      flow: this.currentFlow,
      /** Call from console to see the motion field as arrows overlaid on the
       * precip mask. Opens in a new tab. */
      showMotion: () => {
        if (!this.frame0 || !this.frame1) {
          console.warn("[StormDebug] No frames loaded");
          return;
        }
        if (this.frame0 === this.frame1) {
          console.warn("[StormDebug] Both frames are the same; nothing to visualize");
          return;
        }
        const url = debugVisualize(
          this.frame0.rawData, this.frame1.rawData,
          this.frame0.width, this.frame0.height,
        );
        const w = window.open();
        if (w) {
          w.document.write(`<body style="margin:0;background:#111"><img src="${url}" style="display:block"></body>`);
        } else {
          console.warn("[StormDebug] popup blocked — data URL:", url);
        }
      },
    };
  }

  setTime(t: number) {
    const clamped = Math.max(0, Math.min(1, t));
    if (this._time === clamped) return;
    this._time = clamped;
    this.map?.triggerRepaint();
  }

  /** Inform the layer whether the playhead is being driven by the playback
   * loop. Lets the shader apply animation-aware behavior — currently used
   * to raise the noise-floor cut during playback so light-precip pixels
   * don't pulse as they cross the LUT alpha ramp between frames. */
  setAnimating(animating: boolean) {
    const next = animating ? 1 : 0;
    if (this._animating === next) return;
    this._animating = next;
    this.map?.triggerRepaint();
  }

  setOpacity(o: number) {
    this._opacity = Math.max(0, Math.min(1, o));
    this.map?.triggerRepaint();
  }

  /** Show or hide via MapLibre's layout visibility. The layer instance stays
   *  alive — no GL teardown — so re-showing is instant. */
  setVisible(visible: boolean) {
    if (!this.map) return;
    if (!this.map.getLayer(this.id)) return;
    this.map.setLayoutProperty(this.id, "visibility", visible ? "visible" : "none");
  }

  /** Convenience for callers that don't want to interact with map.removeLayer
   *  directly. Marks the layer disposed so any in-flight ops bail; actual GL
   *  cleanup happens in onRemove(). */
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    if (this.map && this.map.getLayer(this.id)) {
      this.map.removeLayer(this.id);
    }
  }
}